import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Stack,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SyncIcon from "@mui/icons-material/Sync";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PersonIcon from "@mui/icons-material/Person";
import HotelIcon from "@mui/icons-material/Hotel";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import { myApi } from '../../config/axiosConfig';
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import Menu from "@mui/material/Menu";

import { useLocation, useNavigate } from "react-router-dom";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  Tooltip,
} from "recharts";

const DEFAULT_TARGET_MPG = 7;
const MIN_TARGET_MPG = 6;
const LITERS_PER_GALLON = 3.78541;
const KM_PER_MILE = 1.60934;

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

const getVarianceBg = (value) => {
  const num = Number(value || 0);

  if (num < 0) return "#f4cccc";   // rojo claro
  if (num > 0) return "#d9ead3";   // verde claro
  return "#f3f3f3";
};

function calculateRowPotentialSavingsUSD(
  row,
  mxPricePerLiter,
  usPricePerGallon,
  exchangeRate,
  targetMpg
) {
  const distancia = toNumber(row.DistanciaOdo);
  const mpgReal = toNumber(row.MpgReal);

  const colGlls = toNumber(row.galones_colombia);
  const monterreyGlls = toNumber(row.galones_monterrey);
  const oxxoGlls = toNumber(row.CantidadOxxo);
  const lovesGlls = toNumber(row.CantidadLoves);
  const arguiGlls = toNumber(row.CantidadTorre);

  const totalGlls =
    toNumber(row.TotalRealConsumido) ||
    (colGlls + monterreyGlls + oxxoGlls + lovesGlls + arguiGlls);

  if (!distancia || !totalGlls || !mpgReal) return 0;
  if (!targetMpg || targetMpg < MIN_TARGET_MPG) return 0;
  if (mpgReal >= targetMpg) return 0;

  const targetFuelGallons = distancia / targetMpg;
  const wastedGallons = totalGlls - targetFuelGallons;

  if (wastedGallons <= 0) return 0;

  const mxGallons = colGlls + monterreyGlls + oxxoGlls;
  const usGallons = lovesGlls + arguiGlls;

  const mxRatio = totalGlls > 0 ? mxGallons / totalGlls : 0;
  const usRatio = totalGlls > 0 ? usGallons / totalGlls : 0;

  const wastedMxGallons = wastedGallons * mxRatio;
  const wastedUsGallons = wastedGallons * usRatio;

  const wastedMxLiters = wastedMxGallons * LITERS_PER_GALLON;

  const savingsMxMXN = wastedMxLiters * toNumber(mxPricePerLiter);
  const savingsMxUSD =
    toNumber(exchangeRate) > 0 ? savingsMxMXN / toNumber(exchangeRate) : 0;

  const savingsUsUSD = wastedUsGallons * toNumber(usPricePerGallon);

  return Number((savingsMxUSD + savingsUsUSD).toFixed(2));
}

const n1 = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const n0 = new Intl.NumberFormat("en-US");
const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const formatMoney = (value, unitSystem) =>
  new Intl.NumberFormat(unitSystem === "metric" ? "es-MX" : "en-US", {
    style: "currency",
    currency: unitSystem === "metric" ? "MXN" : "USD",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

function CustomDot(props) {
  const { cx, cy } = props;
  if (cx == null || cy == null) return null;

  return (
    <rect
      x={cx - 4}
      y={cy - 4}
      width={8}
      height={8}
      fill="#6f42c1"
      stroke="#6f42c1"
    />
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #ccc",
        p: 1,
        fontSize: 12,
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ fontSize: 12 }}>
        Fuel Eff.: {payload[0]?.value}
      </Typography>
    </Box>
  );
}

function statusColor(v) {
  return (v || "").startsWith("TERM") ? "#fff" : "#333";
}

/* function Cell({ children, bg = "#f3f3f3", align = "right", bold = false, color = "#333", right = true }) {
  return (
    <Box
      sx={{
        px: 0.8,
        py: 0.52,
        fontSize: 15,
        fontWeight: bold ? 800 : 500,
        textAlign: align,
        bgcolor: bg,
        color,
        borderRight: right ? "1px solid #d0d0d0" : "none",
        borderBottom: "1px solid #d0d0d0",
        whiteSpace: "nowrap",
        lineHeight: 1.25,
      }}
    >
      {children}
    </Box>
  );
} */

function Cell({
  children,
  bg = "#f3f3f3",
  align = "right",
  bold = false,
  color = "#333",
  right = true,

  stickyLeft = null,
  stickyRight = null,
  stickyBottom = null,
  zIndex = 1,
}) {
  return (
    <Box
      sx={{
        px: 0.8,
        py: 0.52,
        fontSize: 15,
        fontWeight: bold ? 800 : 500,
        textAlign: align,
        bgcolor: bg,
        color,
        borderRight: right ? "1px solid #d0d0d0" : "none",
        borderBottom: "1px solid #d0d0d0",
        whiteSpace: "nowrap",
        lineHeight: 1.25,

        position:
          stickyLeft !== null || stickyRight !== null || stickyBottom !== null
            ? "sticky"
            : "relative",

        left: stickyLeft,
        right: stickyRight,
        bottom: stickyBottom,

        zIndex,

        boxShadow:
          stickyLeft !== null
            ? "2px 0 6px rgba(0,0,0,.08)"
            : stickyRight !== null
            ? "-2px 0 6px rgba(0,0,0,.08)"
            : "none",
      }}
    >
      {children}
    </Box>
  );
}

function HeaderCell({ children, center = false }) {
  return (
    <Box
      sx={{
        bgcolor: "#2f2f2f",
        color: "#fff",
        fontWeight: 800,
        fontSize: 14,
        px: 0.8,
        py: 0.75,
        textAlign: center ? "center" : "left",
        borderBottom: "2px solid #3b9cff",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Box>
  );
}

function getPeriodRangeFromGroupKey(groupKey, period) {
  if (!groupKey) return null;

  if (period === "Month") {
    const [year, month] = groupKey.split("-").map(Number);
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 0);
    end.setHours(23, 59, 59, 999);

    return { start: start.getTime(), end: end.getTime() };
  }

  if (period === "Week") {
    const [yearPart, weekPart] = groupKey.split("-W");
    const year = Number(yearPart);
    const week = Number(weekPart);

    const jan4 = new Date(year, 0, 4);
    const jan4Day = jan4.getDay() || 7;
    const mondayWeek1 = new Date(jan4);
    mondayWeek1.setDate(jan4.getDate() - jan4Day + 1);

    const start = new Date(mondayWeek1);
    start.setDate(mondayWeek1.getDate() + (week - 1) * 7);
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);

    return { start: start.getTime(), end: end.getTime() };
  }

  const [year, month, day] = groupKey.split("-").map(Number);
  const start = new Date(year, month - 1, day);
  start.setHours(0, 0, 0, 0);

  const end = new Date(year, month - 1, day);
  end.setHours(23, 59, 59, 999);

  return { start: start.getTime(), end: end.getTime() };
}



function buildChartData(rows = [], period = "Month") {
  const grouped = {};

  rows.forEach((row) => {
    if (!row?.date) return;

    const mpg = parseFloat(row.MpgReal);
    if (Number.isNaN(mpg) || mpg <= 0) return;

    const d = new Date(row.date);
    if (Number.isNaN(d.getTime())) return;

    let groupKey = "";
    let label = "";
    let sortValue = 0;

    if (period === "Month") {
      const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
      ];

      groupKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      label = monthNames[d.getMonth()];
      sortValue = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
    } 
    else if (period === "Week") 
    {
        const temp = new Date(d);
        temp.setHours(0, 0, 0, 0);

        const day = temp.getDay() || 7;
        temp.setDate(temp.getDate() + 4 - day);

        const yearStart = new Date(temp.getFullYear(), 0, 1);
        const week = Math.ceil((((temp - yearStart) / 86400000) + 1) / 7);

        groupKey = `${temp.getFullYear()}-W${String(week).padStart(2, "0")}`;
        label = `${week}`;
        sortValue = new Date(temp.getFullYear(), 0, 1 + (week - 1) * 7).getTime();
    } 
    else 
    {
        groupKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

        label = `${d.getDate()}/${String(d.getMonth() + 1).padStart(2, "0")}`;

        sortValue = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    }

    if (!grouped[groupKey]) {
  grouped[groupKey] = {
    groupKey,
    label,
    total: 0,
    count: 0,
    sortValue,
  };
}

    grouped[groupKey].total += mpg;
    grouped[groupKey].count += 1;
  });

  return Object.values(grouped)
    .sort((a, b) => a.sortValue - b.sortValue)
    .map((item) => ({
      label: item.label,
      fuelEff: Number((item.total / item.count).toFixed(2)),
    }));
}

const ChartBlock = React.memo(function ChartBlock({ chartData = [], period = "Month", goal = DEFAULT_TARGET_MPG, unitSystem = "imperial"}) 
{
  
  const goalDisplay = convertEfficiencyFromMpg(goal, unitSystem);
  const baselineDisplay = convertEfficiencyFromMpg(goal - 0.5, unitSystem);

  if (!chartData.length) 
  {
    return (
      <Box
        sx={{
          height: 185,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          //bgcolor: "#efefef",
          bgcolor: "transparent",
        }}
      >
        <Typography>No hay datos para la gráfica</Typography>
      </Box>
    );
  }

  const dataWithLines = chartData.map((item) => ({
    ...item,
    fuelEffDisplay: convertEfficiencyFromMpg(item.fuelEff, unitSystem),
    baseline: baselineDisplay,
    goal: goalDisplay,
  }));

  const minFuel = Math.min(...dataWithLines.map((x) => x.fuelEffDisplay), baselineDisplay, goalDisplay);
  const maxFuel = Math.max(...dataWithLines.map((x) => x.fuelEffDisplay), baselineDisplay, goalDisplay);

  const domainMin = Math.floor((minFuel - 0.1) * 10) / 10;
  const domainMax = Math.ceil((maxFuel + 0.1) * 10) / 10;

  const chartWidth =
  period === "Day"
    ? Math.max(chartData.length * 38, 1200)
    : "100%";

  return (
    <Box
      sx={{
        height: 185,
        position: "relative",
        //bgcolor: "#efefef",
        bgcolor: "transparent",
        px: 1,
        overflowX: period === "Day" ? "auto" : "hidden",
        overflowY: "hidden",
        whiteSpace: "nowrap",

        // Firefox
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(120,120,120,.45) transparent",

        // Chrome / Edge / Opera
        "&::-webkit-scrollbar": {
          height: 8,
        },

        "&::-webkit-scrollbar-track": {
          background: "transparent",
          borderRadius: 20,
        },

        "&::-webkit-scrollbar-thumb": {
          background: "rgba(120,120,120,.45)",
          borderRadius: 20,
          border: "1px solid transparent",
        },

        "&::-webkit-scrollbar-thumb:hover": {
          background: "rgba(90,90,90,.65)",
        },

        // intenta ocultar flechas
        "&::-webkit-scrollbar-button:single-button": {
          display: "none",
          width: 0,
          height: 0,
        },

        "&::-webkit-scrollbar-corner": {
          background: "transparent",
        },
      }}
    >
      <ResponsiveContainer width={chartWidth} height="100%">
      <LineChart
        data={dataWithLines}
        margin={{ top: 18, right: 38, left: 10, bottom: 26 }}
      >
      <CartesianGrid
        stroke="#d0d0d0"
        strokeDasharray={period === "Day" ? "2 4" : "0"}
        vertical={period === "Day"}
        horizontal={false}
        strokeOpacity={0.45}
      />
      <XAxis
        dataKey="label"
        padding={{ left: 30, right: 10 }}
        tick={{ fontSize: 13, fill: "#666" }}
        axisLine={false}
        tickLine={false}
        interval={0}
        height={42}
        label={{
          value: period,
          position: "insideBottom",
          offset: -10,
          style: { fill: "#444", fontSize: 14, fontWeight: 700 },
        }}
      />
      <YAxis
        domain={[domainMin, domainMax]}
        tick={{ fontSize: 14, fill: "#666", fontWeight: 700 }}
        axisLine={false}
        tickLine={false}
        width={38}
      />
      <Tooltip
        content={({ active, payload, label }) => {
          if (!active || !payload || !payload.length) return null;

          return (
            <Box sx={{ bgcolor: "#fff", border: "1px solid #ccc", p: 1, fontSize: 12 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{label}</Typography>
              <Typography sx={{ fontSize: 12 }}>
                Fuel Eff.: {n1.format(payload[0]?.value)} {efficiencyLabel(unitSystem)}
              </Typography>
            </Box>
          );
        }}
      />
      <ReferenceLine
        y={goalDisplay}
        stroke="#15b826"
        strokeWidth={3}
        label={{
          value: `${goalDisplay.toFixed(2)} ${efficiencyLabel(unitSystem)}`,
          position: "insideTopLeft",
          fill: "#333",
          fontSize: 14,
        }}
      />
      <ReferenceLine
        y={baselineDisplay}
        stroke="#58a8ff"
        strokeWidth={2}
        strokeDasharray="6 6"
        label={{
          value: baselineDisplay.toFixed(2),
          position: "insideTopLeft",
          fill: "#2b8cff",
          fontSize: 14,
        }}
      />

      <Line
        type="stepAfter"
        dataKey="fuelEffDisplay"
        stroke="#2b8cff"
        strokeWidth={3}
        dot={<CustomDot />}
        activeDot={{ r: 5 }}
        isAnimationActive={false}

        style={{
          filter: "drop-shadow(0 0 4px rgba(43,140,255,.35))",
        }}
      />
    </LineChart>
  </ResponsiveContainer>
</Box>
);
});

function parseHours(value) 
{
  if (value == null || value === "") return 0;

  const str = String(value).trim();

  // formato hh:mm:ss
  if (str.includes(":")) {
    const parts = str.split(":").map(Number);

    const h = parts[0] || 0;
    const m = parts[1] || 0;
    const s = parts[2] || 0;

    return h + (m / 60) + (s / 3600);
  }

  // decimal normal
  const num = Number(str);
  return Number.isFinite(num) ? num : 0;
}

function buildPeriodTableData(
  rows = [],
  period = "Month",
  mxPricePerLiter = 0,
  usPricePerGallon = 0,
  exchangeRate = 0,
  targetMpg = DEFAULT_TARGET_MPG
) 
{
  const grouped = {};

  rows.forEach((row) => {
    if (!row?.date) return;
    if (row.DistanciaOdo === "INICIAL") return;

    const d = new Date(row.date);
    if (Number.isNaN(d.getTime())) return;

    const distancia = row.DistanciaOdo == null ? 0 : Number(row.DistanciaOdo || 0);

    const colGlls = Number(row.galones_colombia || 0);
    const monterreyGlls = Number(row.galones_monterrey || 0);
    const gasomaxGlls = Number(row.galones_gasomax || 0);
    const oxxoGlls = Number(row.CantidadOxxo || 0);
    const lovesGlls = Number(row.CantidadLoves || 0);
    const arguindeguiGlls = Number(row.CantidadTorre || 0);
    const totalGlls = Number(row.TotalRealConsumido || 0);
    const variance = Number(row.Diferencia || 0);

    const idleHours = parseHours(row.pc_idletime);
    const totalTravelHours = parseHours(row.pc_total_time_traveled);

    const idlePercent =
      totalTravelHours > 0
        ? (idleHours / totalTravelHours) * 100
        : 0;

    const rowPotentialSavings = calculateRowPotentialSavingsUSD(
      row,
      mxPricePerLiter,
      usPricePerGallon,
      exchangeRate,
      targetMpg
    );

    let groupKey = "";
    let label = "";
    let sortValue = 0;

    if (period === "Month") {
      const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
      ];

      groupKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      label = monthNames[d.getMonth()];
      sortValue = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
    } 
    else if (period === "Week") 
    {
      const temp = new Date(d);
      temp.setHours(0, 0, 0, 0);

      const day = temp.getDay() || 7;
      temp.setDate(temp.getDate() + 4 - day);

      const yearStart = new Date(temp.getFullYear(), 0, 1);
      const week = Math.ceil((((temp - yearStart) / 86400000) + 1) / 7);

      groupKey = `${temp.getFullYear()}-W${String(week).padStart(2, "0")}`;
      label = `Week ${week}`;
      sortValue = new Date(temp.getFullYear(), 0, 1 + (week - 1) * 7).getTime();
    } 
    else 
    {
      groupKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      label = `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
      sortValue = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    }

    if (!grouped[groupKey]) {
      grouped[groupKey] = {
        groupKey,
        label,
        sortValue,
        fuelingEvents: 0,
        variance: 0,
        colGlls: 0,
        monterreyGlls: 0,
        gasomaxGlls: 0,
        oxxoGlls: 0,
        lovesGlls: 0,
        arguindeguiGlls: 0,
        totalGlls: 0,
        distance: 0,
        fuelEff: 0,
        totalPotentialSavings: 0,
        idleHours: 0,
        travelHours: 0,
      };
    }

    grouped[groupKey].fuelingEvents += 1;
    grouped[groupKey].variance += variance;
    grouped[groupKey].colGlls += colGlls;
    grouped[groupKey].monterreyGlls += monterreyGlls;
    grouped[groupKey].gasomaxGlls += gasomaxGlls;
    grouped[groupKey].oxxoGlls += oxxoGlls;
    grouped[groupKey].lovesGlls += lovesGlls;
    grouped[groupKey].arguindeguiGlls += arguindeguiGlls;
    grouped[groupKey].totalGlls += totalGlls;
    grouped[groupKey].distance += distancia;
    grouped[groupKey].totalPotentialSavings += rowPotentialSavings;
    grouped[groupKey].idleHours += idleHours;
    grouped[groupKey].travelHours += totalTravelHours;
  });

  const rowsOut = Object.values(grouped)
    .sort((a, b) => a.sortValue - b.sortValue)
    .map((item) => ({
      groupKey: item.groupKey,
      label: item.label,
      fuelingEvents: item.fuelingEvents,
      variance: Number(item.variance.toFixed(2)),
      colGlls: Number(item.colGlls.toFixed(2)),
      monterreyGlls: Number(item.monterreyGlls.toFixed(2)),
      gasomaxGlls: Number(item.gasomaxGlls.toFixed(2)), 
      oxxoGlls: Number(item.oxxoGlls.toFixed(2)),
      lovesGlls: Number(item.lovesGlls.toFixed(2)),
      arguindeguiGlls: Number(item.arguindeguiGlls.toFixed(2)),
      totalGlls: Number(item.totalGlls.toFixed(2)),
      distance: Number(item.distance.toFixed(2)),
      fuelEff:
        item.totalGlls > 0 && item.distance > 0
          ? Number((item.distance / item.totalGlls).toFixed(2))
          : 0,
      totalPotentialSavings: Number(item.totalPotentialSavings.toFixed(2)),
      idleHours: Number(item.idleHours.toFixed(2)),
      travelHours: Number(item.travelHours.toFixed(2)),
      idlePercent:
        item.travelHours > 0
          ? Number(((item.idleHours / item.travelHours) * 100).toFixed(0))
          : 0,
    }));

  const totals = rowsOut.reduce(
    (acc, item) => {
      acc.fuelingEvents += item.fuelingEvents;
      acc.variance += item.variance;
      acc.colGlls += item.colGlls;
      acc.monterreyGlls += item.monterreyGlls;
      acc.gasomaxGlls += item.gasomaxGlls;
      acc.oxxoGlls += item.oxxoGlls;
      acc.lovesGlls += item.lovesGlls;
      acc.arguindeguiGlls += item.arguindeguiGlls;
      acc.totalGlls += item.totalGlls;
      acc.distance += item.distance;
      acc.totalPotentialSavings += item.totalPotentialSavings;
      acc.idleHours += item.idleHours;
      acc.travelHours += item.travelHours;
      return acc;
    },
    {
      fuelingEvents: 0,
      variance: 0,
      colGlls: 0,
      monterreyGlls: 0,
      gasomaxGlls: 0,
      oxxoGlls: 0,
      lovesGlls: 0,
      arguindeguiGlls: 0,
      totalGlls: 0,
      distance: 0,
      totalPotentialSavings: 0,
      idleHours: 0,
      travelHours: 0,
    }
  );

  rowsOut.push({
    label: "Total",
    fuelingEvents: totals.fuelingEvents,
    variance: Number(totals.variance.toFixed(2)),
    colGlls: Number(totals.colGlls.toFixed(2)),
    monterreyGlls: Number(totals.monterreyGlls.toFixed(2)),
    gasomaxGlls: Number(totals.gasomaxGlls.toFixed(2)),
    oxxoGlls: Number(totals.oxxoGlls.toFixed(2)),
    lovesGlls: Number(totals.lovesGlls.toFixed(2)),
    arguindeguiGlls: Number(totals.arguindeguiGlls.toFixed(2)),
    totalGlls: Number(totals.totalGlls.toFixed(2)),
    distance: Number(totals.distance.toFixed(2)),
    fuelEff:
      totals.totalGlls > 0 && totals.distance > 0
        ? Number((totals.distance / totals.totalGlls).toFixed(2))
        : 0,
    totalPotentialSavings: Number(totals.totalPotentialSavings.toFixed(2)),
    isTotal: true,
    idleHours: Number(totals.idleHours.toFixed(2)),
    idlePercent:
      totals.travelHours > 0
        ? Number(((totals.idleHours / totals.travelHours) * 100).toFixed(0))
        : 0,
  });

  return rowsOut;
}

function buildTruckRanking(rows = []) {
  const grouped = {};

  rows.forEach((row) => {
    if (!row?.asset) return;
    if (row.DistanciaOdo === "INICIAL") return;

    const unit = String(row.asset).trim();
    const make = row.brand || row.Make || "";

    const distance = Number(row.DistanciaOdo || 0);
    const fuel = Number(row.TotalRealConsumido || 0);

    if (!(distance > 0) || !(fuel > 0)) return;

    if (!grouped[unit]) {
      grouped[unit] = {
        truck: unit,
        make,
        distance: 0,
        fuel: 0,
      };
    }

    grouped[unit].distance += distance;
    grouped[unit].fuel += fuel;
  });

  return Object.values(grouped)
    .map((item) => ({
      truck: item.truck,
      make: item.make,
      fuelEff: Number((item.distance / item.fuel).toFixed(2)),
    }))
    .filter((x) => x.fuelEff > 0)
    .sort((a, b) => a.fuelEff - b.fuelEff); // menor primero
}

const statusOptions = [
  { value: '', label: '' },
  { value: 'INFO', label: 'INFO - Conversación informativa (primer repaso, sin falta grave)' },
  { value: 'ACK', label: 'ACK - Se explicó info y el driver reconoció resultados (sin firma)' },
  { value: 'SIGN', label: 'SIGN - Firmó PDF de mejora / compromiso' },
  { value: 'W1', label: 'W1 - Primera advertencia (malos números confirmados)' },
  { value: 'W2', label: 'W2 - Segunda advertencia (reincidencia)' },
  { value: 'W3', label: 'W3 - Tercera advertencia (última antes de acción)' },
  { value: 'IMP', label: 'IMP - En plan de mejora activo' },
  { value: 'MON', label: 'MON - En monitoreo cercano (post-reset)' },
  { value: 'TRN', label: 'TRN - Reentrenamiento requerido' },
  { value: 'FNL', label: 'FNL - Advertencia final emitida' },
  { value: 'RCMD', label: 'RCMD - Recomendado reemplazo de driver' },
  { value: 'TERM', label: 'TERM - Terminado / baja del driver' },
  { value: '1.IMP', label: '1. IMP - Mejora confirmada / desempeño corregido' },
  { value: '2.EXC', label: '2. EXC - Desempeño sobresaliente' },
  { value: '3.CLR', label: '3. CLR - Caso cerrado (mejora sostenida)' },
];

const statusMap = statusOptions.reduce((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

function getFullStatusLabel(status) {
  return statusMap[status || ""] || status || "";
}

function statusBg(v) {
  const s = String(v || "").trim();

  if (s.startsWith("TERM")) return "#ff1616";
  if (s.startsWith("RCMD")) return "#e7c700";
  if (s.startsWith("FNL")) return "#ff9d9d";
  if (s.startsWith("W3")) return "#ffb3b3";
  if (s.startsWith("W2")) return "#ffd1a6";
  if (s.startsWith("W1")) return "#fff2cc";
  if (s.startsWith("SIGN")) return "#f2f2f2";
  if (s.startsWith("ACK")) return "#e8f0fe";
  if (s.startsWith("INFO")) return "#e8f0fe";
  if (s.startsWith("IMP")) return "#d9ead3";
  if (s.startsWith("1.IMP")) return "#cfe2f3";
  if (s.startsWith("2.EXC")) return "#b6e3b6";
  if (s.startsWith("3.CLR")) return "#d9ead3";
  if (s.startsWith("MON")) return "#d9eaf7";
  if (s.startsWith("TRN")) return "#f4cccc";

  return "transparent";
}

function parseDriver(value) {
  const raw = String(value || "").trim();

  if (!raw) {
    return {
      code: "",
      name: "",
      raw: "",
    };
  }

  const separatorIndex = raw.indexOf("-");

  // Cuando el dato no contiene código
  if (separatorIndex === -1) {
    return {
      code: raw.toUpperCase(),
      name: raw,
      raw,
    };
  }

  const code = raw.slice(0, separatorIndex).trim().toUpperCase();
  const name = raw.slice(separatorIndex + 1).trim();

  return {
    code,
    name: name || code,
    raw,
  };
}

function getDriverKey(value) {
  const { name } = parseDriver(value);

  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
}

function buildDriverRanking(rows = []) {
  const grouped = {};

  rows.forEach((row) => {
    if (!row?.driver) return;
    if (row.DistanciaOdo === "INICIAL") return;

    const { name: driverName } = parseDriver(row.driver);
    const driverKey = getDriverKey(row.driver);

    if (!driverKey) return;

    const mpg = Number(row.MpgReal);
    const status = String(row.seguimiento_estatus || "").trim();
    const rowDate = row.ultima_fecha || row.fecha_modifica || row.fecha_alta || row.date;

    if (!Number.isFinite(mpg) || mpg <= 0) return;

    if (!grouped[driverKey]) {
      grouped[driverKey] = {
        driverKey,
        driverName,
        distance: 0,
        fuel: 0,
        latestStatus: "",
        latestStatusDate: null,
      };
    }

    grouped[driverKey].distance += Number(row.DistanciaOdo || 0);
    grouped[driverKey].fuel += Number(row.TotalRealConsumido || 0);

    if (status) {
      const currentDate = rowDate ? new Date(rowDate).getTime() : 0;
      const savedDate = grouped[driverKey].latestStatusDate
        ? new Date(grouped[driverKey].latestStatusDate).getTime()
        : 0;

      if (!grouped[driverKey].latestStatus || currentDate >= savedDate) {
        grouped[driverKey].latestStatus = status;
        grouped[driverKey].latestStatusDate = rowDate;
      }
    }
  });

  return Object.values(grouped)
    .map((item) => ({
      driverKey: item.driverKey,
      driver: item.driverName,
      fuelEff:
        item.fuel > 0
          ? Number((item.distance / item.fuel).toFixed(2))
          : 0,
      statusCode: item.latestStatus || "",
      statusLabel: getFullStatusLabel(item.latestStatus || ""),
    }))
    .filter((x) => x.fuelEff > 0)
    .sort((a, b) => b.fuelEff - a.fuelEff) // mejores arriba
    .map((item, index) => ({
      rank: index + 1,
      ...item,
    }));
}

function getPeriodKey(row, period = "Month") {
  if (!row?.date) return null;

  const d = new Date(row.date);
  if (Number.isNaN(d.getTime())) return null;

  if (period === "Month") {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }

  if (period === "Week") {
    const temp = new Date(d);
    temp.setHours(0, 0, 0, 0);

    const day = temp.getDay() || 7;
    temp.setDate(temp.getDate() + 4 - day);

    const yearStart = new Date(temp.getFullYear(), 0, 1);
    const week = Math.ceil((((temp - yearStart) / 86400000) + 1) / 7);

    return `${temp.getFullYear()}-W${String(week).padStart(2, "0")}`;
  }

  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getPeriodLabel(row, period = "Month") {
  if (!row?.date) return "";

  const d = new Date(row.date);
  if (Number.isNaN(d.getTime())) return "";

  if (period === "Month") {
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    return monthNames[d.getMonth()];
  }

  if (period === "Week") {
    const temp = new Date(d);
    temp.setHours(0, 0, 0, 0);

    const day = temp.getDay() || 7;
    temp.setDate(temp.getDate() + 4 - day);

    const yearStart = new Date(temp.getFullYear(), 0, 1);
    const week = Math.ceil((((temp - yearStart) / 86400000) + 1) / 7);

    return `Week ${week}`;
  }

  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
}

const toggleSelection = (value, setter) => {
  setter((prev) =>
    prev.includes(value)
      ? prev.filter((item) => item !== value)
      : [...prev, value]
  );
};

function convertFuel(value, unitSystem) {
  const num = Number(value || 0);
  return unitSystem === "metric"
    ? num * LITERS_PER_GALLON
    : num;
}

function convertDistance(value, unitSystem) {
  const num = Number(value || 0);
  return unitSystem === "metric"
    ? num * KM_PER_MILE
    : num;
}

function convertEfficiencyFromMpg(value, unitSystem) {
  const num = Number(value || 0);
  return unitSystem === "metric"
    ? num * 0.425144 // km/L
    : num;           // MPG
}

function convertMoneyFromUSD(value, unitSystem, exchangeRate) {
  const num = Number(value || 0);
  return unitSystem === "metric"
    ? num * Number(exchangeRate || 0) // MXN
    : num;                            // USD
}

function fuelLabel(unitSystem) {
  return unitSystem === "metric" ? "L" : "Gal";
}

function distanceLabel(unitSystem) {
  return unitSystem === "metric" ? "KM" : "Miles";
}

function efficiencyLabel(unitSystem) {
  return unitSystem === "metric" ? "KM/L" : "MPG";
}

function currencyLabel(unitSystem) {
  return unitSystem === "metric" ? "MXN" : "USD";
}



export default function MasterDashboardPowerBI() 
{

    const navigate = useNavigate();

    const location = useLocation();

    const currentYear = new Date().getFullYear();

    const [selectedYear, setSelectedYear] = useState(currentYear);

    const initialCountryMode =
      location.state?.countryMode || "MEX";

    const [countryMode, setCountryMode] =
      useState(initialCountryMode);

    const isUSA = countryMode === "USA";

    //const [usaTractors, setUsaTractors] = useState([]);
    const [usaTractors, setUsaTractors] = useState({});

    const [activeDriversMap, setActiveDriversMap] = useState({});

    const [anchorElView, setAnchorElView] = useState(null);

    const openView = Boolean(anchorElView);

    const handleOpenView = (event) => {
      setAnchorElView(event.currentTarget);
    };

    const handleCloseView = () => {
      setAnchorElView(null);
    };

    const [anchorElUnits, setAnchorElUnits] = useState(null);
    const openUnits = Boolean(anchorElUnits);

    const [anchorElSource, setAnchorElSource] = useState(null);
    const openSource = Boolean(anchorElSource);

    const handleOpenUnits = (e) => setAnchorElUnits(e.currentTarget);
    const handleCloseUnits = () => setAnchorElUnits(null);

    const handleOpenSource = (e) => setAnchorElSource(e.currentTarget);
    const handleCloseSource = () => setAnchorElSource(null);

    const [refreshing, setRefreshing] = useState(false);

    function buildDateTime(dateValue, timeValue) {
      if (!dateValue) return null;

      const datePart = String(dateValue).split("T")[0];
      const timePart = timeValue || "00:00:00";

      return `${datePart}T${timePart}`;
    }

    function calculateUsaPotentialSavingsUSD(row, usPricePerGallon, targetMpg) {
      const distance = toNumber(row.TotalDistanceMiles);
      const fuel = toNumber(row.TotalFuelGallons);
      const mpg = fuel > 0 ? distance / fuel : 0;

      if (!distance || !fuel || !mpg) return 0;
      if (!targetMpg || targetMpg < MIN_TARGET_MPG) return 0;
      if (mpg >= targetMpg) return 0;

      const targetFuelGallons = distance / targetMpg;
      const wastedGallons = fuel - targetFuelGallons;

      return wastedGallons > 0
        ? Number((wastedGallons * toNumber(usPricePerGallon)).toFixed(2))
        : 0;
    }

    function normalizeUsaRows(rows = [], usPricePerGallon = 0, targetMpg = DEFAULT_TARGET_MPG, usaMap = {}) {
      return rows.map((row) => {
        const distance = toNumber(row.TotalDistanceMiles);
        const fuel = toNumber(row.TotalFuelGallons);
        const mpg = fuel > 0 ? distance / fuel : 0;

        const idleMinutes = toNumber(row.IntertripIdleTimeMinutes);
        const engineMinutes = toNumber(row.EngineTimeMinutes);

        const vehicle = String(row.VehicleID || "")
          .trim()
          .replace(/^0+/, "");

        const fleetId = String(usaMap?.[vehicle]?.fleet_id || "").toUpperCase();

        let fleetGroup = "EBT";

        if (fleetId.includes("LOCAL")) {
          fleetGroup = "LOCAL";
        } else if (fleetId.includes("XFER") || fleetId.includes("TRANSFER")) {
          fleetGroup = "TRANSFER";
        }

        return {
          ...row,

          sourceCountry: "USA",

          date: buildDateTime(row.ExtractEndDate, row.ExtractEndTime),
          asset: row.VehicleID || "",
          driver: row.DriverID || "",
          company: fleetGroup,
          fleet_id: fleetId,

          DistanciaOdo: distance,
          TotalRealConsumido: fuel,
          MpgReal: mpg,

          galones_colombia: 0,
          CantidadOxxo: 0,
          CantidadLoves: fuel,
          CantidadTorre: 0,

          Diferencia: 0,

          pc_idletime: idleMinutes / 60,
          pc_total_time_traveled: engineMinutes / 60,

          usaIdleMinutes: idleMinutes,
          usaEngineMinutes: engineMinutes,
          usaPotentialSavingsUSD: calculateUsaPotentialSavingsUSD(row, usPricePerGallon, targetMpg),

          seguimiento_estatus: "",
          brand: "",
          Make: "",
        };
      });
    }

    function normalizeSamsaraVehicleRows(rows = []) {
      return rows.map((row) => {
        const distance = toNumber(row.millas);
        const fuel = toNumber(row.galones);

        return {
          ...row,
          sourceCountry: "USA",
          sourceKind: "samsaraVehicle",

          date: `${row.report_date}T00:00:00`,
          asset: String(row.asset || "").trim(),
          driver: "",
          company: String(row.company || "EBT").trim(),

          DistanciaOdo: distance,
          TotalRealConsumido: fuel,
          MpgReal: fuel > 0 ? distance / fuel : 0,

          CantidadLoves: fuel,
          galones_colombia: 0,
          CantidadOxxo: 0,
          CantidadTorre: 0,
          Diferencia: 0,

          pc_idletime: toNumber(row.minutos_ralenti) / 60,
          pc_total_time_traveled: toNumber(row.minutos_motor) / 60,

          usaIdleMinutes: toNumber(row.minutos_ralenti),
          usaEngineMinutes: toNumber(row.minutos_motor),

          seguimiento_estatus: "",
          brand: "",
          Make: "",
        };
      });
    }

    function normalizeSamsaraDriverRows(rows = []) {
      return rows.map((row) => {
        const distance = toNumber(row.millas);
        const fuel = toNumber(row.galones);

        return {
          ...row,
          sourceCountry: "USA",
          sourceKind: "samsaraDriver",

          date: `${row.report_date}T00:00:00`,
          asset: String(row.asset || "").trim() || "UNIDAD NO DETERMINADA",
          driver: String(row.driver || "").trim(),

          driverAliases: [
            row.driver,
            row.samsara_driver_id,
          ].filter(Boolean),

          driver_name: row.driver_name || "",

          company:
            String(row.fleet_group || "").trim().toUpperCase() ||
            "GRUPO NO DET",

          DistanciaOdo: distance,
          TotalRealConsumido: fuel,
          MpgReal: fuel > 0 ? distance / fuel : 0,

          CantidadLoves: fuel,
          galones_colombia: 0,
          CantidadOxxo: 0,
          CantidadTorre: 0,
          Diferencia: 0,

          pc_idletime: toNumber(row.minutos_ralenti) / 60,
          pc_total_time_traveled: toNumber(row.minutos_motor) / 60,

          usaIdleMinutes: toNumber(row.minutos_ralenti),
          usaEngineMinutes: toNumber(row.minutos_motor),

          seguimiento_estatus: "",
          brand: "",
          Make: "",
        };
      });
    }

    const [rankingAssets, setRankingAssets] = useState(null);
    const [rankingDrivers, setRankingDrivers] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sorted, setSorted] = useState("FECHA"); 
    const [colorT, setColorT] = useState("TODO"); 
    const [typeOf, setTypeOf] = useState("TODO");
    const [company, setCompany] = useState("TODAS");

    const [selectedDrivers, setSelectedDrivers] = useState([]);
    const [selectedTrucks, setSelectedTrucks] = useState([]);
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [selectedPeriods, setSelectedPeriods] = useState([]);

    const [targetMpg, setTargetMpg] = useState(DEFAULT_TARGET_MPG);
    const [targetInput, setTargetInput] = useState(String(DEFAULT_TARGET_MPG.toFixed(2)));

    const [unitSystem, setUnitSystem] = useState("imperial");

    const displayedGoal = convertEfficiencyFromMpg(targetMpg, unitSystem);
    const displayedMinGoal = convertEfficiencyFromMpg(MIN_TARGET_MPG, unitSystem);

    const [expandedDays, setExpandedDays] = useState([]);

/*     const toggleExpandedDay = (groupKey) => {
  setExpandedDays((prev) =>
    prev.includes(groupKey)
      ? prev.filter((x) => x !== groupKey)
      : [...prev, groupKey]
  );
}; */

    const toggleExpandedDay = (groupKey) => {
  setExpandedDays((prev) =>
    prev.includes(groupKey) ? [] : [groupKey]
  );
};

    const clearCrossFilters = () => {
  setSelectedDrivers([]);
  setSelectedTrucks([]);
  setSelectedCompanies([]);
  setSelectedPeriods([]);
};

const togglePeriodRange = (range) => {
  if (!range) return;

  setSelectedPeriods((prev) => {
    const exists = prev.some(
      (item) => item.start === range.start && item.end === range.end
    );

    if (exists) {
      return prev.filter(
        (item) => !(item.start === range.start && item.end === range.end)
      );
    }

    return [...prev, range];
  });
};

  const [data, setData] = useState([]);

  const [usaDriverData, setUsaDriverData] = useState([]);

  const [period, setPeriod] = useState("Month");

  const availableYears = useMemo(() => {
    const years = [...new Set(
      data
        .map((row) => {
          const d = new Date(row.date);
          return Number.isNaN(d.getTime()) ? null : d.getFullYear();
        })
        .filter(Boolean)
    )];

    return years.sort((a, b) => b - a);
  }, [data]);

const dataWithoutCompanyFilter = useMemo(() => {
  const sourceRows = countryMode === "USA"
    ? usaDriverData
    : data;

  if (!Array.isArray(sourceRows)) return [];

  return sourceRows.filter((row) => {
    if (!row) return false;

    if (countryMode === "USA") {
      const unit = String(row.asset ?? "").trim().toUpperCase();

      const group = String(
        row.sourceKind === "samsaraDriver"
          ? row.fleet_group ?? ""
          : row.company ?? ""
      ).trim().toUpperCase();

      const invalidValues = [
        "",
        "UNKNOWN",
        "SIN ATRIBUIR",
        "UNIDAD NO DETERMINADA",
        "GRUPO NO DETERMINADO",
        "GRUPO NO DET",
      ];

      if (
        invalidValues.includes(unit) ||
        invalidValues.includes(group)
      ) {
        return false;
      }
    }

    const driver = String(row.driver || "").trim();
    const driverId = driver.toUpperCase();

    if (
      countryMode === "USA" &&
      (
        !driverId ||
        driverId === "SHOP" ||
        activeDriversMap[driverId] !== true
      )
    ) {
      return false;
    }

    const driverKey = getDriverKey(driver);
    const truck = String(row.asset || "").trim();
    const date = row.date ? new Date(row.date) : null;
    const rowTime = date ? date.getTime() : NaN;
    const rowYear = date ? date.getFullYear() : NaN;

    const matchDriver =
      selectedDrivers.length === 0 ||
      selectedDrivers.includes(driverKey);

    const matchTruck =
      selectedTrucks.length === 0 ||
      selectedTrucks.includes(truck);

    const matchYear =
      !selectedYear ||
      rowYear === Number(selectedYear);

    const matchPeriod =
      selectedPeriods.length === 0 ||
      (
        Number.isFinite(rowTime) &&
        selectedPeriods.some(
          range => rowTime >= range.start && rowTime <= range.end
        )
      );

    return matchDriver && matchTruck && matchYear && matchPeriod;
  });
}, [
  countryMode,
  usaDriverData,
  data,
  activeDriversMap,
  selectedDrivers,
  selectedTrucks,
  selectedYear,
  selectedPeriods,
]);

const filteredData = useMemo(() => {
  if (selectedCompanies.length === 0) {
    return dataWithoutCompanyFilter;
  }

  return dataWithoutCompanyFilter.filter((row) => {
    const company = String(
      isUSA ? row?.company : row?.Asset_trsnport_line
    ).trim() || "UNKNOWN";

    return selectedCompanies.includes(company);
  });
}, [dataWithoutCompanyFilter, selectedCompanies, isUSA]);

const isUsaDriverFocus =
  countryMode === "USA" &&
  selectedDrivers.length > 0;

const dashboardRows = filteredData;


    const searchRows = useMemo(() => {
      if (!data || !Array.isArray(data)) return [];

      return [...new Set(
        data
          .map(row => (row.driver || "").trim())
          .filter(Boolean)
      )].sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" }));
    }, [data]);

    const [dieselMX, setDieselMX] = useState(0);
    const [dieselUS, setDieselUS] = useState(0);
    const [exchangeRate, setExchangeRate] = useState(0);

    const getDiesel = async () => 
    {
        const res = await myApi.get('/cargasFuel/getDieselPriceMX');

        setDieselMX(Number(res.data.price || 0));
    };

    const getDieselUS = async () => 
    {
        const res = await myApi.get('/cargasFuel/getDieselPriceUS');

        setDieselUS(Number(res.data.price || 0));
    };

    const getRate = async () => 
    {
        const res = await myApi.get('/cargasFuel/getExchangeRateMX');

        setExchangeRate(Number(res.data.rate || 0));
    };

    const getUsaTractors = async () => {
      try {
        const res = await myApi.get('/omni/getTractoresUSA');

        if (res.data.success) {
          const tractorMap = {};

          res.data.data.forEach((item) => {
            const tractor = String(item.tractor || "")
              .trim()
              .replace(/^0+/, "");

            tractorMap[tractor] = item;
          });

          setUsaTractors(tractorMap);
          return tractorMap;
        }

        return {};
      } catch (error) {
        console.error('Error getting USA tractors:', error);
        return {};
      }
    };

    const getDriversActivos = async () => 
    {
      try 
      {
        const res = await myApi.get("/omni/getDriversActivos");

        const map = {};

        if (res.data.success) 
        {
          res.data.data.forEach((driver) => {
            map[String(driver.driver_id || "").trim().toUpperCase()] = true;
          });
        }

        setActiveDriversMap(map);
        return map;
      } 
      catch (error) 
      {
        console.error("Error getting active drivers:", error);
        setActiveDriversMap({});
        return {};
      }
    };

    const handleRefresh = async (silent = false) => {
      getDiesel();
      getDieselUS();
      getRate();

      let tractors = usaTractors;

      if (countryMode === "USA") {
        await getDriversActivos();
        tractors = await getUsaTractors();
      }

      await fetch(
        null,
        null,
        null,
        null,
        rankingAssets,
        rankingDrivers,
        countryMode,
        tractors,
        silent
      );
    };

    const fetch = async (unidad = null, fi = null, ff = null, tipo = null, rankingAssetsParam = rankingAssets, rankingDriversParam = rankingDrivers, sourceParam = countryMode, usaTractorsParam = usaTractors, silent = false) => 
    {
        if(silent) 
          setRefreshing(true);
        else 
          setLoading(true);
        try 
        {
            const datosEnviados = { unidad, fi, ff, tipo, colorT, sorted, typeOf, company, rankingAssets: rankingAssetsParam, rankingDrivers: rankingDriversParam };

            //const response = await myApi.post('/cargasFuel/getDashMPGTract', datosEnviados);

/*             const endpoint =
              sourceParam === "USA"
                ? "/omni/getCargasOmni"
                : "/cargasFuel/getDashMPGTract"; */

            const endpoint =
              sourceParam === "USA"
                ? "/omni/getCargasKpiUSA"
                : "/cargasFuel/getDashMPGTract";

/*             const payload =
              sourceParam === "USA"
                ? []
                : datosEnviados; */
            
            const payload =
              sourceParam === "USA"
                ? { ...datosEnviados, version: 2 }
                : datosEnviados;

            const response = await myApi.post(endpoint, payload);

            console.log(response)
            /* if(response.data.success)
                setData(response.data.data);
            else
                setData([]); */
            if (response?.data?.success) 
            {
              /* const rawRows = Array.isArray(response.data.data)
                ? response.data.data
                : [];

              / const rows =
                sourceParam === "USA"
                  ? normalizeUsaRows(rawRows, dieselUS, targetMpg)
                  : rawRows; 

              let rows = rawRows;

              if (sourceParam === "USA")
              {
                  const usaMap = usaTractorsParam || {};

                  const filteredRows = rawRows.filter((row) => {
                    const vehicle = String(row.VehicleID || "")
                      .trim()
                      .replace(/^0+/, "");

                    return !!usaMap[vehicle];
                  });

                  rows = normalizeUsaRows(
                    filteredRows,
                    dieselUS,
                    targetMpg,
                    usaMap
                  );
                }

              setData(rows); */
              if (
                sourceParam === "USA" &&
                Number(response.data.version) === 2
              ) 
              {
                const usaMap = usaTractorsParam || {};
                const sourceData = response.data.data || {};

                const omnitracsRows = Array.isArray(sourceData.omnitracs)
                  ? sourceData.omnitracs
                  : [];

                const historicalRows = normalizeUsaRows(
                  omnitracsRows.filter((row) => {
                    const vehicle = String(row.VehicleID || "")
                      .trim()
                      .replace(/^0+/, "");

                    return !!usaMap[vehicle];
                  }),
                  dieselUS,
                  targetMpg,
                  usaMap
                ).map((row) => ({
                  ...row,
                  sourceKind: "omnitracs",
                }));

                const samsaraVehicleRows = normalizeSamsaraVehicleRows(
                  Array.isArray(sourceData.vehiculosSamsara)
                    ? sourceData.vehiculosSamsara
                    : []
                );

                const samsaraDriverRows = normalizeSamsaraDriverRows(
                  Array.isArray(sourceData.operadoresSamsara)
                    ? sourceData.operadoresSamsara
                    : []
                );

                // Totales, gráfica, tabla y camiones: solo vehículos.
                setData([
                  ...historicalRows,
                  ...samsaraVehicleRows,
                ]);

                // Ranking de conductores: solo consumo por operador.
                setUsaDriverData([
                  ...historicalRows,
                  ...samsaraDriverRows,
                ]);
              } else {
                const rawRows = Array.isArray(response.data.data)
                  ? response.data.data
                  : [];

                let rows = rawRows;

                if (sourceParam === "USA") {
                  const usaMap = usaTractorsParam || {};

                  const filteredRows = rawRows.filter((row) => {
                    const vehicle = String(row.VehicleID || "")
                      .trim()
                      .replace(/^0+/, "");

                    return !!usaMap[vehicle];
                  });

                  rows = normalizeUsaRows(
                    filteredRows,
                    dieselUS,
                    targetMpg,
                    usaMap
                  );
                }

                setData(rows);
                setUsaDriverData(rows);
              }
            } 
            else 
              setData([]);
        } 
        catch(error) 
        {
            console.error('Error fetching fuel loads:', error);
            setData([]);
            setUsaDriverData([]);
        }
        finally 
        {
            if(silent) 
              setRefreshing(false);
            else 
              setLoading(false);
        }
    };

/*     useEffect(() => {
        fetch();
        getDiesel();
        getDieselUS();
        getRate();
        getUsaTractors();
    }, []); */

    /*
    useEffect(() => {
    const init = async () => {
        getDiesel();
        getDieselUS();
        getRate();

        if (countryMode === "USA") {
            const tractors = await getUsaTractors();

            fetch(
                null,
                null,
                null,
                null,
                rankingAssets,
                rankingDrivers,
                "USA",
                tractors
            );
        } else {
            fetch(
                null,
                null,
                null,
                null,
                rankingAssets,
                rankingDrivers,
                "MEX"
            );
        }
    };

    init();
}, []);*/
  useEffect(() => {
    handleRefresh(false);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      handleRefresh(true);
    }, 10 * 60 * 1000);

    return () => clearInterval(interval);
  }, [countryMode, rankingAssets, rankingDrivers, usaTractors]);

//const chartData = useMemo(() => buildChartData(filteredData, period), [filteredData, period]);

/* const chartData = useMemo(
  () => buildChartData(dashboardRows, period),
  [dashboardRows, period]
); */



const [tableSortField, setTableSortField] = useState("label");
const [tableSortDirection, setTableSortDirection] = useState("asc");

const handleTableSort = (field) => {
  const finalField =
    period === "Day" &&
    expandedDays.length > 0 &&
    field === "fuelingEvents"
      ? "unit"
      : field;

  if (tableSortField === finalField) {
    setTableSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  } else {
    setTableSortField(finalField);
    setTableSortDirection(finalField === "label" ? "asc" : "asc");
  }
};

const periodTable = useMemo(
  () => buildPeriodTableData(dashboardRows, period, dieselMX, dieselUS, exchangeRate, targetMpg),
  [dashboardRows, period, dieselMX, dieselUS, exchangeRate, targetMpg]
);
const chartData = useMemo(() => {
  if (countryMode === "USA") {
    return periodTable
      .filter((row) => !row.isTotal)
      .map((row) => ({
        label: row.label,
        fuelEff: row.fuelEff,
      }));
  }

  return buildChartData(dashboardRows, period);
}, [countryMode, periodTable, dashboardRows, period]);
const periodTableRows = periodTable.filter((r) => !r.isTotal);
const periodTableTotals = periodTable.find((r) => r.isTotal) || {
  fuelingEvents: 0,
  variance: 0,
  colGlls: 0,
  monterreyGlls: 0,
  gasomaxGlls: 0,
  oxxoGlls: 0,
  lovesGlls: 0,
  arguindeguiGlls: 0,
  totalGlls: 0,
  distance: 0,
  fuelEff: 0,
  totalPotentialSavings: 0,
  idlePercent: 0,
  idleHours: 0,
  travelHours: 0,
};

const totalColFuelDisplay = convertFuel(periodTableTotals.colGlls, unitSystem);
const totalMonterreyFuelDisplay = convertFuel(periodTableTotals.monterreyGlls, unitSystem);
const totalGasomaxFuelDisplay = convertFuel(periodTableTotals.gasomaxGlls, unitSystem);
const totalOxxoFuelDisplay = convertFuel(periodTableTotals.oxxoGlls, unitSystem);
const totalLovesFuelDisplay = convertFuel(periodTableTotals.lovesGlls, unitSystem);
const totalArguiFuelDisplay = convertFuel(periodTableTotals.arguindeguiGlls, unitSystem);
const totalTotalFuelDisplay = convertFuel(periodTableTotals.totalGlls, unitSystem);

const totalDistanceDisplay = convertDistance(periodTableTotals.distance, unitSystem);

const totalFuelEffDisplay = convertEfficiencyFromMpg(
  periodTableTotals.fuelEff,
  unitSystem
);

const totalSavingsDisplay = convertMoneyFromUSD(
  periodTableTotals.totalPotentialSavings,
  unitSystem,
  exchangeRate
);

const totalVarianceDisplay = convertFuel(
  periodTableTotals.variance,
  unitSystem
);

const sortedPeriodTableRows = useMemo(() => {
  const rowsCopy = [...periodTableRows];

  rowsCopy.sort((a, b) => {
    let aValue = a[tableSortField];
    let bValue = b[tableSortField];

    if (tableSortField === "label") {
      if (period === "Month") {
        const monthOrder = [
          "January", "February", "March", "April", "May", "June",
          "July", "August", "September", "October", "November", "December"
        ];

        aValue = monthOrder.indexOf(a.label);
        bValue = monthOrder.indexOf(b.label);
      } else if (period === "Week") {
        aValue = Number(String(a.label).replace("Week ", ""));
        bValue = Number(String(b.label).replace("Week ", ""));
      } else {
        const [am, ad] = String(a.label).split("/").map(Number);
        const [bm, bd] = String(b.label).split("/").map(Number);

        aValue = (am * 100) + ad;
        bValue = (bm * 100) + bd;
      }
    }

    if (typeof aValue === "string" && typeof bValue === "string") {
      return tableSortDirection === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return tableSortDirection === "asc"
      ? Number(aValue) - Number(bValue)
      : Number(bValue) - Number(aValue);
  });

  return rowsCopy;
}, [periodTableRows, tableSortField, tableSortDirection, period]);


function SortHeader({
  children,
  field,
  activeField,
  direction,
  onClick,
  align = "left",
  right = true,
  variant = "light",
  stickyLeft = null,
  stickyRight = null,
  zIndex = 5,
}) {
  const isActive = activeField === field;

  const styles =
    variant === "dark"
      ? {
          bg: "#2f2f2f",
          color: "#fff",
          borderRight: right ? "1px solid #555" : "none",
          borderBottom: "2px solid #3b9cff",
          arrow: "#fff",
        }
      : {
          bg: "#f1f1f1",
          color: "#111",
          borderRight: right ? "1px solid #d0d0d0" : "none",
          borderBottom: "1px solid #d0d0d0",
          arrow: "#666",
        };

  return (
    <Box
      onClick={() => onClick(field)}
      sx={{
  px: 0.8,
  py: 0.9,
  fontSize: 14,
  fontWeight: 800,
  bgcolor: styles.bg,
  color: styles.color,
  borderRight: styles.borderRight,
  borderBottom: styles.borderBottom,

position: "sticky",
top: 0,

left: stickyLeft,
right: stickyRight,

zIndex:
  stickyLeft !== null
    ? zIndex
    : stickyRight !== null
    ? 20
    : 15,

  whiteSpace: "nowrap",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent:
    align === "center" ? "center" : "space-between",
}}
    >
      <span>{children}</span>

      <span
        style={{
          fontSize: 11,
          color: styles.arrow,
          opacity: isActive ? 1 : 0.35,
        }}
      >
        {isActive
          ? direction === "asc"
            ? "▲"
            : "▼"
          : "↕"}
      </span>
    </Box>
  );
}

const [truckSortField, setTruckSortField] = useState("fuelEff");
const [truckSortDirection, setTruckSortDirection] = useState("asc");

const handleTruckSort = (field) => {
  if (truckSortField === field) {
    setTruckSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  } else {
    setTruckSortField(field);
    setTruckSortDirection(field === "make" ? "asc" : "asc");
  }
};

//const truckRanking = useMemo(() => buildTruckRanking(filteredData), [filteredData]);

const truckRanking = useMemo(
  () => buildTruckRanking(filteredData),
  [filteredData]
);

const sortedTruckRanking = useMemo(() => {
  const rowsCopy = [...truckRanking];

  rowsCopy.sort((a, b) => {
    let aValue = a[truckSortField];
    let bValue = b[truckSortField];

    if (truckSortField === "truck") {
      const aNum = Number(aValue);
      const bNum = Number(bValue);

      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
        return truckSortDirection === "asc" ? aNum - bNum : bNum - aNum;
      }

      return truckSortDirection === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    }

    if (truckSortField === "make") {
      return truckSortDirection === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    }

    return truckSortDirection === "asc"
      ? Number(aValue) - Number(bValue)
      : Number(bValue) - Number(aValue);
  });

  return rowsCopy;
}, [truckRanking, truckSortField, truckSortDirection]);

const truckMakeWidth = useMemo(() => {
  const longest = truckRanking.reduce((max, row) => {
    const len = String(row.make || "").length;
    return len > max ? len : max;
  }, 4);

  return Math.max(110, longest * 10);
}, [truckRanking]);

const truckGridColumns = isUSA
  ? "80px 95px"
  : `80px 95px ${truckMakeWidth}px`;
const truckGridMinWidth = isUSA
  ? 175
  : 80 + 95 + truckMakeWidth;

const getFuelColor = (value) => {
  if (value < 6) return "#efc2c2";     // rojo claro
  if (value < 7) return "#efe2a0";     // amarillo
  return "#bfe6b8";                    // verde
};

const companyRanking = useMemo(() => {

  const grouped = {};

  const sourceRows = dataWithoutCompanyFilter;

  sourceRows.forEach((row) => {
    if (!row) return;
    if (row.DistanciaOdo === "INICIAL") return;

    const company = String(
      isUSA ? row?.company : row?.Asset_trsnport_line
    ).trim() || "UNKNOWN";

    const distance = Number(row.DistanciaOdo || 0);
    const fuel = Number(row.TotalRealConsumido || 0);

    if (distance === 0 && fuel === 0) return;

    if (!grouped[company]) {
      grouped[company] = {
        company,
        distance: 0,
        fuel: 0,
      };
    }

    grouped[company].distance += distance;
    grouped[company].fuel += fuel;
  });

  return Object.values(grouped)
    .map((item) => ({
      company: item.company,
      fuelEff: item.fuel > 0
        ? Number((item.distance / item.fuel).toFixed(2))
        : 0,
    }))
    .sort((a, b) => a.company.localeCompare(b.company));

}, [dataWithoutCompanyFilter, isUSA]);

 
  const [fleet, setFleet] = useState("All");
  const [currency, setCurrency] = useState("USD");
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("MAIN");

/* const filteredSearch = useMemo(() => {
  if (!Array.isArray(data)) return [];

  const driversMap = new Map();

  data.forEach((row) => {
    const { name } = parseDriver(row?.driver);
    const key = getDriverKey(row?.driver);

    if (!key || !name) return;

    if (!driversMap.has(key)) {
      driversMap.set(key, {
        key,
        name,
      });
    }
  });

  const uniqueDrivers = Array.from(driversMap.values()).sort(
    (a, b) =>
      a.name.localeCompare(b.name, "es", {
        sensitivity: "base",
      })
  );

  const searchText = search.trim().toLowerCase();

  if (!searchText) return uniqueDrivers;

  return uniqueDrivers.filter((driver) =>
    driver.name.toLowerCase().includes(searchText)
  );
}, [data, search]); */

const filteredSearch = useMemo(() => {
  const sourceRows =
    countryMode === "USA"
      ? filteredData
      : data;

  if (!Array.isArray(sourceRows)) return [];

  const driversMap = new Map();

  sourceRows.forEach((row) => {
    const driverId = String(row?.driver || "")
      .trim()
      .toUpperCase();

    // Excluye SHOP y cualquier fila sin operador.
    if (!driverId || driverId === "SHOP") return;

    // En USA, muestra únicamente operadores activos.
    if (
      countryMode === "USA" &&
      activeDriversMap[driverId] !== true
    ) {
      return;
    }

    const key = getDriverKey(driverId);

    if (!key) return;

    const aliases = [
      driverId,
      row?.samsara_driver_id,
      ...(Array.isArray(row?.driverAliases)
        ? row.driverAliases
        : []),
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean);

    const name = String(
      row?.driver_name || driverId
    ).trim();

    if (!driversMap.has(key)) {
      driversMap.set(key, {
        key,
        driverId,
        name,
        aliases,
      });
      return;
    }

    const current = driversMap.get(key);

    current.aliases = [
      ...new Set([
        ...current.aliases,
        ...aliases,
      ]),
    ];

    // Si Samsara ya entregó el nombre, se usa sobre el código solo.
    if (
      current.name === current.driverId &&
      name !== driverId
    ) {
      current.name = name;
    }
  });

  const searchText = search.trim().toLowerCase();

  return Array.from(driversMap.values())
    .filter((item) => {
      if (!searchText) return true;

      return [
        item.driverId,
        item.name,
        ...item.aliases,
      ].some((value) =>
        String(value).toLowerCase().includes(searchText)
      );
    })
    .sort((a, b) =>
      a.name.localeCompare(b.name, "es", {
        sensitivity: "base",
      })
    );
}, [
  countryMode,
  usaDriverData,
  data,
  search,
  activeDriversMap,
]);

  const getFuelEffColor = (value) => {
  const num = Number(value || 0);

  if (num < 6) return "#f4cccc";      // rojo suave
  if (num >= 6 && num < 7) return "#efe3a1"; // amarillo
  return "#b6e3b6";                  // verde
};

const [companySortField, setCompanySortField] = useState("company");
const [companySortDirection, setCompanySortDirection] = useState("asc");

const handleCompanySort = (field) => {
  if (companySortField === field) {
    setCompanySortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  } else {
    setCompanySortField(field);
    setCompanySortDirection("asc");
  }
};

const sortedCompanyRanking = useMemo(() => {
  const rowsCopy = [...companyRanking];

  rowsCopy.sort((a, b) => {
    const aValue = a[companySortField];
    const bValue = b[companySortField];

    if (companySortField === "company") {
      return companySortDirection === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    }

    return companySortDirection === "asc"
      ? Number(aValue) - Number(bValue)
      : Number(bValue) - Number(aValue);
  });

  return rowsCopy;
}, [companyRanking, companySortField, companySortDirection]);

//const driverRanking = useMemo(() => buildDriverRanking(filteredData), [filteredData]);

const driverRankingSource = filteredData;

const driverRanking = useMemo(
  () => buildDriverRanking(driverRankingSource),
  [driverRankingSource]
);

const [driverSort, setDriverSort] = useState({
  field: "fuelEff",
  direction: "desc",
});

const handleDriverSort = (field) => {
  setDriverSort((prev) => ({
    field,
    direction:
      prev.field === field && prev.direction === "asc" ? "desc" : "asc",
  }));
};

const sortedDriverRanking = useMemo(() => {
  const rows = [...driverRanking];

  rows.sort((a, b) => {
    let result = 0;

    switch (driverSort.field) {
      case "rank":
        result = Number(a.rank || 0) - Number(b.rank || 0);
        break;

      case "driver":
        result = String(a.driver || "").localeCompare(String(b.driver || ""));
        break;

      case "fuelEff":
        result = Number(a.fuelEff || 0) - Number(b.fuelEff || 0);
        break;

      case "statusLabel":
        result = String(a.statusLabel || "").localeCompare(String(b.statusLabel || ""));
        break;

      default:
        result = 0;
        break;
    }

    return driverSort.direction === "asc" ? result : -result;
  });

  return rows;
}, [driverRanking, driverSort]);



const driverGridColumns = isUSA
  ? "72px 300px 110px"
  : "72px 300px 110px 360px";

const driverGridMinWidth = isUSA ? 482 : 842;

const overallFuelEff = useMemo(() => {
  if (!Array.isArray(dashboardRows) || !dashboardRows.length) return 0;

  let totalDistance = 0;
  let totalFuel = 0;

  dashboardRows.forEach((row) => {
    if (row?.DistanciaOdo === "INICIAL") return;

    totalDistance += Number(row?.DistanciaOdo || 0);
    totalFuel += Number(row?.TotalRealConsumido || 0);
  });

  return totalFuel > 0
    ? Number((totalDistance / totalFuel).toFixed(2))
    : 0;
}, [dashboardRows]);

const handleTargetChange = (e) => {
  setTargetInput(e.target.value);
};

const handleTargetBlur = () => {
  const parsed = Number(targetInput);

  if (!Number.isFinite(parsed)) {
    setTargetMpg(DEFAULT_TARGET_MPG);
    setTargetInput(
      convertEfficiencyFromMpg(DEFAULT_TARGET_MPG, unitSystem).toFixed(2)
    );
    return;
  }

  const parsedMpg =
    unitSystem === "metric"
      ? parsed / 0.425144
      : parsed;

  const safeValue = Math.max(MIN_TARGET_MPG, parsedMpg);
  const roundedValue = Number(safeValue.toFixed(2));

  setTargetMpg(roundedValue);
  setTargetInput(
    convertEfficiencyFromMpg(roundedValue, unitSystem).toFixed(2)
  );
};

useEffect(() => {
  setTargetInput(convertEfficiencyFromMpg(targetMpg, unitSystem).toFixed(2));
}, [unitSystem, targetMpg]);

useEffect(() => {
  if (availableYears.length && !availableYears.includes(selectedYear)) {
    setSelectedYear(availableYears[0]);
  }
}, [availableYears, selectedYear]);

const DAY_COL = 150;
const UNITS_COL = 110;
const MPG_COL = 170;

//const hasOpenDayDetail = period === "Day" && expandedDays.length > 0;

const hasOpenDayDetail =
  period === "Day" &&
  expandedDays.length > 0 &&
  !isUsaDriverFocus;

  return (
    <>
    <Box sx={{ minHeight: "100vh", bgcolor: "#ececec", p: 1 }}>
      <Paper
        sx={{

          height: "calc(100vh - 180px)",
          bgcolor: "#ececec",
          borderRadius: 0,
          border: "1px solid #dadada",
          p: 1,
          display: "grid",
          gridTemplateRows: "1fr",
          gap: 1,
          overflow: "hidden",
        }}
      >
        {loading ? (
            <Box
            sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#ececec",
            }}
            >
            <Box
                sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 34px)",
                gap: "4px",
                }}
            >
                {[0,1,2,3,4,5,6,7,8].map((item) => (
                <Box
                    key={item}
                    sx={{
                    width: 34,
                    height: 34,
                    bgcolor:
                        item === 7
                        ? "#5b9bd5"
                        : item === 4
                        ? "transparent"
                        : "#1f497d",
                    animation: "powerPulse 1.1s infinite ease-in-out",
                    animationDelay: `${item * 0.05}s`,
                    }}
                />
                ))}
            </Box>

            <style>
                {`
                @keyframes powerPulse {
                    0% { opacity:.4; transform:scale(.9); }
                    50% { opacity:1; transform:scale(1); }
                    100% { opacity:.4; transform:scale(.9); }
                }
                `}
            </style>
            </Box>
        ) : (
        <Box
          sx={{
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: "2.7fr 0.6fr 1.7fr",
            gap: 1,
            overflow: "hidden",
          }}
        >
          <Box
  sx={{
    minWidth: 0,
    display: "grid",
    //gridTemplateRows: "48px 248px 1fr 58px",
    //gridTemplateRows: "92px 248px 1fr 58px",
    gridTemplateRows: "92px 190px 1fr 58px",
    gap: 1,
    overflow: "hidden",
  }}
>
            
<Box
  sx={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1,
    overflow: "visible",
  }}
>
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    {["Month", "Week", "Day"].map((item) => (
      <Button
        key={item}
        variant={period === item ? "contained" : "outlined"}
        onClick={() => setPeriod(item)}
        sx={{
          minWidth: 102,
          height: 36,
          textTransform: "none",
          fontWeight: 800,
        }}
      >
        {item}
      </Button>
    ))}
  </Box>

  <Paper
  sx={{
    width: 320,
    height: 82,

    px: 2.5,

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    borderRadius: "22px",

    background:
      "linear-gradient(135deg, #dff7df 0%, #c5ebc8 45%, #b6e3b6 100%)",

    border: "1px solid rgba(76,175,80,.45)",

    boxShadow:
      "0 10px 24px rgba(0,0,0,.12), inset 0 1px 0 rgba(255,255,255,.55)",

    position: "relative",
    overflow: "hidden",

    flexShrink: 0,

    "&::before": {
      content: '""',
      position: "absolute",
      top: -40,
      right: -40,
      width: 120,
      height: 120,
      borderRadius: "50%",
      background: "rgba(255,255,255,.18)",
    },
  }}
>
<Box
  sx={{
    zIndex: 2,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    gap: 1.2,
    width: "100%",
    height: "100%",
  }}
>
<Typography
  sx={{
    fontSize: 28,
    fontWeight: 900,
    color: "#2f5f2f",
    lineHeight: 1,
    whiteSpace: "nowrap",

    display: "flex",
    alignItems: "center",

    height: "100%",
  }}
>
  GOAL
</Typography>

  <TextField
    variant="standard"
    value={targetInput}
    onChange={handleTargetChange}
    onBlur={handleTargetBlur}
    inputProps={{
      style: {
        textAlign: "left",
        fontWeight: 900,
      },
    }}
    sx={{
      width: 145,

      "& .MuiInput-root:before": {
        borderBottom: "none",
      },

      "& .MuiInput-root:after": {
        borderBottom: "none",
      },

      "& input": {
        fontSize: 50,
        lineHeight: 1,
        padding: 0,
        color: "#1f1f1f",
      },
    }}
  />

  <Typography
    sx={{
      fontSize: 24,
      fontWeight: 900,
      color: "#2e2e2e",
      letterSpacing: 1,
      lineHeight: 1,
      whiteSpace: "nowrap",
      mb: 0.3,
    }}
  >
    {efficiencyLabel(unitSystem)}
  </Typography>
</Box>
</Paper>
</Box>

 



<Paper
  sx={{
    borderRadius: "22px",

    background:
      "linear-gradient(180deg, #f7f7f7 0%, #eeeeee 100%)",

    border: "1px solid rgba(0,0,0,.08)",

    boxShadow:
      "0 10px 28px rgba(0,0,0,.10), inset 0 1px 0 rgba(255,255,255,.75)",

    overflow: "hidden",

    position: "relative",
  }}
>
  <ChartBlock chartData={chartData} period={period} goal={targetMpg} unitSystem={unitSystem}/>
</Paper>

<Paper
  sx={{
    borderRadius: 0,
    border: "1px solid #d5d5d5",
    bgcolor: "#f4f4f4",
    minHeight: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  }}
>

<Box
  sx={{
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    position: "relative",
    "&::-webkit-scrollbar": {
        height: 8,
        width: 8,
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#bdbdbd",
        borderRadius: 8,
      },
      "&::-webkit-scrollbar-track": {
        background: "#efefef",
      },
  }}
>
<Box
  sx={{
    display: "grid",
    /* gridTemplateColumns: isUSA
      ? "1.15fr 0.95fr 1fr 1.1fr 1fr 1.25fr 0.9fr 1fr"
      : "1.15fr 0.95fr 1fr 1fr 1fr 1fr 1fr 1fr 1.1fr 1fr 1.25fr 1fr 0.9fr 1fr",
    minWidth: 1250, */
/*     gridTemplateColumns: isUSA
      ? `${DAY_COL}px ${UNITS_COL}px 135px 150px ${MPG_COL}px 190px 90px 110px`
      : `${DAY_COL}px ${UNITS_COL}px 145px 155px 135px 135px 165px 145px 160px ${MPG_COL}px 200px 145px 90px 110px`,
    minWidth: isUSA ? 1025 : 1920, */

    gridTemplateColumns: isUSA
      ? `${DAY_COL}px ${UNITS_COL}px 135px 150px ${MPG_COL}px 190px 90px 110px`
      : `${DAY_COL}px ${UNITS_COL}px 145px 155px 145px 135px 135px 165px 145px 160px ${MPG_COL}px 200px 145px 90px 110px`,
    minWidth: isUSA ? 1025 : 2065,
  }}
>
<SortHeader
  field="label"
  activeField={tableSortField}
  direction={tableSortDirection}
  onClick={handleTableSort}
  stickyLeft={0}
  zIndex={50}
>
  {period === "Month" ? "MONTH" : period === "Week" ? "WEEK" : "DAY"}
</SortHeader>

<SortHeader
  field={hasOpenDayDetail ? "unit" : "fuelingEvents"}
  activeField={tableSortField}
  direction={tableSortDirection}
  onClick={handleTableSort}
  align="center"
  stickyLeft={hasOpenDayDetail ? DAY_COL : null}
  zIndex={hasOpenDayDetail ? 60 : 15}
>
  {/* {hasOpenDayDetail ? "Units" : "Fueling Events"} */}
  {hasOpenDayDetail
  ? "Units"
  : isUSA
    ? "Records"
    : "Fueling Events"}
</SortHeader>

                    {!isUSA && (
                      <SortHeader
                      field="colGlls"
                      activeField={tableSortField}
                      direction={tableSortDirection}
                      onClick={handleTableSort}
                      >
                      Colombia Fuel ({fuelLabel(unitSystem)})
                      </SortHeader>
                    )}

                    {!isUSA && (
                      <SortHeader 
                        field="monterreyGlls" 
                        activeField={tableSortField} 
                        direction={tableSortDirection} 
                        onClick={handleTableSort}>
                        Monterrey Fuel ({fuelLabel(unitSystem)})
                      </SortHeader>
                    )}

                    {!isUSA && (
                      <SortHeader 
                        field="gasomaxGlls" 
                        activeField={tableSortField} 
                        direction={tableSortDirection} 
                        onClick={handleTableSort}>
                        Gasomax Fuel ({fuelLabel(unitSystem)})
                      </SortHeader>
                    )}

                    

                    {!isUSA && (
                    <SortHeader
                    field="oxxoGlls"
                    activeField={tableSortField}
                    direction={tableSortDirection}
                    onClick={handleTableSort}
                    >
                    Oxxo Fuel ({fuelLabel(unitSystem)})
                    </SortHeader>
                    )}

                    {!isUSA && (
                    <SortHeader
                    field="lovesGlls"
                    activeField={tableSortField}
                    direction={tableSortDirection}
                    onClick={handleTableSort}
                    >
                    Loves Fuel ({fuelLabel(unitSystem)})
                    </SortHeader>
                    )}

                    {!isUSA && (
                    <SortHeader
                    field="arguindeguiGlls"
                    activeField={tableSortField}
                    direction={tableSortDirection}
                    onClick={handleTableSort}
                    >
                    Arguidengui Fuel ({fuelLabel(unitSystem)})
                    </SortHeader>
                    )}

                    <SortHeader
                    field="totalGlls"
                    activeField={tableSortField}
                    direction={tableSortDirection}
                    onClick={handleTableSort}
                    bg="#d7efd4"
                    >
                    Total Fuel ({fuelLabel(unitSystem)})
                    </SortHeader>

                    <SortHeader
                    field="distance"
                    activeField={tableSortField}
                    direction={tableSortDirection}
                    onClick={handleTableSort}
                    bg="#bdd7ef"
                    >
                    Distance ({distanceLabel(unitSystem)})
                    </SortHeader>

                    <SortHeader
  field="fuelEff"
  activeField={tableSortField}
  direction={tableSortDirection}
  onClick={handleTableSort}
  align="center"
  right={false}
  stickyRight={0}
  zIndex={10}
>
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      lineHeight: 1.05,
    }}
  >
    <span>Fuel Efficiency</span>

    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        opacity: 0.9
      }}
    >
      ({efficiencyLabel(unitSystem)})
    </span>
  </Box>
</SortHeader>

                    <SortHeader
                    field="totalPotentialSavings"
                    activeField={tableSortField}
                    direction={tableSortDirection}
                    onClick={handleTableSort}
                    align="center"
                    right={false}
                  >
                    <Box
                      sx={{
                        whiteSpace: "normal",
                        lineHeight: 1.1,
                        textAlign: "center",
                        fontSize: 13,
                      }}
                    >
                      Total Potential
                      <br />
                      Savings ({currencyLabel(unitSystem)})
                    </Box>
                  </SortHeader>

                  {!isUSA && (
                  <SortHeader
                  field="variance"
                  activeField={tableSortField}
                  direction={tableSortDirection}
                  onClick={handleTableSort}
                  align="center"
                  right={false}
                >
                  Fuel Variance ({fuelLabel(unitSystem)})
                </SortHeader>
                  )}

                <SortHeader
                  field="idlePercent"
                  activeField={tableSortField}
                  direction={tableSortDirection}
                  onClick={handleTableSort}
                  align="center"
                >
                  IDLE %
                </SortHeader>

                <SortHeader
                  field="idleHours"
                  activeField={tableSortField}
                  direction={tableSortDirection}
                  onClick={handleTableSort}
                  align="center"
                  right={false}
                >
                  IDLE (Hrs)
                </SortHeader>

                    {sortedPeriodTableRows.map((r) => {
  const periodRange = getPeriodRangeFromGroupKey(r.groupKey, period);

  const isSelected = selectedPeriods.some(
    (item) =>
      item.start === periodRange?.start && item.end === periodRange?.end
  );

  const colFuelDisplay = convertFuel(r.colGlls, unitSystem);
  const monterreyFuelDisplay = convertFuel(r.monterreyGlls, unitSystem);
  const gasomaxFuelDisplay = convertFuel(r.gasomaxGlls, unitSystem);
  const oxxoFuelDisplay = convertFuel(r.oxxoGlls, unitSystem);
  const lovesFuelDisplay = convertFuel(r.lovesGlls, unitSystem);
  const arguiFuelDisplay = convertFuel(r.arguindeguiGlls, unitSystem);
  const totalFuelDisplay = convertFuel(r.totalGlls, unitSystem);
  const distanceDisplay = convertDistance(r.distance, unitSystem);
  const fuelEffDisplay = convertEfficiencyFromMpg(r.fuelEff, unitSystem);
  const savingsDisplay = convertMoneyFromUSD(r.totalPotentialSavings, unitSystem, exchangeRate);
  const varianceDisplay = convertFuel(r.variance, unitSystem);

/*   let detailRows =
  period === "Day"
    ? filteredData.filter((row) =>
        getPeriodKey(row, "Day") === r.groupKey &&
        row.DistanciaOdo !== "INICIAL"
      )
    : []; */

  let detailRows =
  period === "Day" && !isUsaDriverFocus
    ? dashboardRows.filter((row) =>
        getPeriodKey(row, "Day") === r.groupKey &&
        row.DistanciaOdo !== "INICIAL"
      )
    : [];

  if (period === "Day" && tableSortField === "unit") {
    detailRows = [...detailRows].sort((a, b) => {
      const aUnit = String(a.asset || "");
      const bUnit = String(b.asset || "");

      const aNum = Number(aUnit.replace(/\D/g, ""));
      const bNum = Number(bUnit.replace(/\D/g, ""));

      const result =
        !Number.isNaN(aNum) && !Number.isNaN(bNum)
          ? aNum - bNum
          : aUnit.localeCompare(bUnit);

      return tableSortDirection === "asc" ? result : -result;
    });
  }
  const isExpanded = expandedDays.includes(r.groupKey);

  return (
    <React.Fragment key={r.groupKey}>
<Cell
  align="left"
  bold
  bg={isSelected ? "#dbeafe" : "#f3f3f3"}
  stickyLeft={0}
  zIndex={40}
>
  <Box
    sx={{
      width: "100%",
      display: "flex",
      alignItems: "center",
      gap: 0.7,
    }}
  >
    {period === "Day" && (
      <Box
        component="span"
        onClick={(e) => {
          e.stopPropagation();
          toggleExpandedDay(r.groupKey);
        }}
        sx={{
          fontWeight: 900,
          cursor: "pointer",
          minWidth: 18,
        }}
      >
        {isExpanded ? "⊟" : "⊞"}
      </Box>
    )}

    <Box
      onClick={() => togglePeriodRange(periodRange)}
      sx={{
        cursor: "pointer",
        flex: 1,
      }}
    >
      {r.label}
    </Box>
  </Box>
</Cell>

<Cell
  align="center"
  stickyLeft={hasOpenDayDetail ? DAY_COL : null}
  zIndex={hasOpenDayDetail ? 45 : 1}
>
  {n0.format(r.fuelingEvents || 0)}
</Cell>
      {!isUSA && <Cell>{n1.format(colFuelDisplay)}</Cell>}
      {!isUSA && <Cell>{n1.format(monterreyFuelDisplay)}</Cell>}
      {!isUSA && <Cell>{n1.format(gasomaxFuelDisplay)}</Cell>}
      {!isUSA && <Cell>{n1.format(oxxoFuelDisplay)}</Cell>}
      {!isUSA && <Cell>{n1.format(lovesFuelDisplay)}</Cell>}
      {!isUSA && <Cell>{n1.format(arguiFuelDisplay)}</Cell>}
      <Cell bg="#d7efd4">{n1.format(totalFuelDisplay)}</Cell>
      <Cell bg="#bdd7ef" right={false}>{n1.format(distanceDisplay)}</Cell>


      <Cell
  align="center"
  right={false}
  bg={getFuelEffColor(r.fuelEff)}
  stickyRight={0}
  zIndex={2}
>
  {n1.format(fuelEffDisplay)} {efficiencyLabel(unitSystem)}
</Cell>

      <Cell align="right" right={false} bg="#8cff66">
        {formatMoney(savingsDisplay, unitSystem)}
      </Cell>
      {!isUSA &&
      <Cell
  align="right"
  right={false}
  bg={getVarianceBg(r.variance)}
>
  {n1.format(convertFuel(r.variance, unitSystem))}
</Cell>
}
<Cell align="center" bg="#1cff1c">
  {r.idlePercent}%
</Cell>

<Cell align="right">
  {n1.format(r.idleHours)}
</Cell>

          {period === "Day" && isExpanded && detailRows.map((detail, index) => {
              const detailColFuel = convertFuel(detail.galones_colombia, unitSystem);
              const detailMonterreyFuel = convertFuel(detail.galones_monterrey, unitSystem);
              const detailGasomaxFuel = convertFuel(detail.galones_gasomax, unitSystem);
              const detailOxxoFuel = convertFuel(detail.CantidadOxxo, unitSystem);
              const detailLovesFuel = convertFuel(detail.CantidadLoves, unitSystem);
              const detailArguiFuel = convertFuel(detail.CantidadTorre, unitSystem);
              const detailTotalFuel = convertFuel(detail.TotalRealConsumido, unitSystem);
              const detailDistanceRaw = Number(detail.DistanciaOdo);
              const detailMpgRaw = Number(detail.MpgReal);

              const detailDistance =
                Number.isFinite(detailDistanceRaw)
                  ? convertDistance(detailDistanceRaw, unitSystem)
                  : 0;

              const detailFuelEff =
                Number.isFinite(detailMpgRaw)
                  ? convertEfficiencyFromMpg(detailMpgRaw, unitSystem)
                  : 0;
              const detailVarianceRaw = Number(detail.Diferencia);

              const detailVariance =
                Number.isFinite(detailVarianceRaw)
                  ? convertFuel(detailVarianceRaw, unitSystem)
                  : 0;

              const detailDate = detail.date ? new Date(detail.date) : null;
/*               const detailTime =
                detailDate && !Number.isNaN(detailDate.getTime())
                  ? detailDate.toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                      second: "2-digit",
                    })
                  : ""; */
              const detailTime =
                detail.sourceKind === "samsaraVehicle" ||
                detail.sourceKind === "samsaraDriver"
                  ? ""
                  : detailDate && !Number.isNaN(detailDate.getTime())
                    ? detailDate.toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                        second: "2-digit",
                      })
                    : "";
              const detailIdleHours = parseHours(detail.pc_idletime);
              const detailTravelHours = parseHours(detail.pc_total_time_traveled);

              const detailIdlePercent =
                detailTravelHours > 0
                  ? Number(((detailIdleHours / detailTravelHours) * 100).toFixed(0))
                  : 0;

              const detailPotentialSavings = calculateRowPotentialSavingsUSD(
                detail,
                dieselMX,
                dieselUS,
                exchangeRate,
                targetMpg
              );

              const detailSavingsDisplay = convertMoneyFromUSD(
                detailPotentialSavings,
                unitSystem,
                exchangeRate
              );

              return (
                <React.Fragment key={`${r.groupKey}-detail-${index}`}>
                  <Cell
                    align="left"
                    bg="#e9e9e9"
                    stickyLeft={0}
                    zIndex={40}
                  >
                    <Box sx={{ pl: 2, fontWeight: 700 }}>
                      {detailTime}
                    </Box>
                  </Cell>

                  <Cell
                    align="center"
                    bold
                    stickyLeft={DAY_COL}
                    zIndex={45}
                  >
                    {detail.asset || "-"}
                  </Cell>
                  {!isUSA && <Cell bg="#e9e9e9">{n1.format(detailColFuel)}</Cell>}
                  {!isUSA && <Cell bg="#e9e9e9">{n1.format(detailMonterreyFuel)}</Cell>}
                  {!isUSA && <Cell bg="#e9e9e9">{n1.format(detailGasomaxFuel)}</Cell>}
                  {!isUSA && <Cell bg="#e9e9e9">{n1.format(detailOxxoFuel)}</Cell>}
                  {!isUSA && <Cell bg="#e9e9e9">{n1.format(detailLovesFuel)}</Cell>}
                  {!isUSA && <Cell bg="#e9e9e9">{n1.format(detailArguiFuel)}</Cell>}
                  <Cell bg="#ccffcc">{n1.format(detailTotalFuel)}</Cell>
                  <Cell bg="#d9eaf7" right={false}>
  {detailDistance > 0 ? n1.format(detailDistance) : "-"}
</Cell>

                  <Cell
  align="center"
  right={false}
  bg={getFuelEffColor(detail.MpgReal)}
  stickyRight={0}
  zIndex={10}
>
  {detailFuelEff > 0
  ? `${n1.format(detailFuelEff)} ${efficiencyLabel(unitSystem)}`
  : "-"}
</Cell>

                  <Cell align="right" right={false} bg={detailPotentialSavings > 0 ? "#e2f0d9" : "#e9e9e9"}>
                    {detailPotentialSavings > 0 ? formatMoney(detailSavingsDisplay, unitSystem) : "-"}
                  </Cell>

                  {!isUSA && 
                  <Cell align="right" right={false} bg={getVarianceBg(detail.Diferencia)}>
                    {Number.isFinite(detailVarianceRaw)
                    ? n1.format(detailVariance)
                    : "-"}
                  </Cell>
                  }

                  <Cell align="center" bg="#e9e9e9">
                    {detailTravelHours > 0 ? `${detailIdlePercent}%` : "-"}
                  </Cell>

                  <Cell align="right" bg="#e9e9e9">
                    {detailIdleHours > 0 ? n1.format(detailIdleHours) : "-"}
                  </Cell>
                </React.Fragment>
              );
            })}

    </React.Fragment>
  );
})}


<Cell
  align="left"
  bold
  stickyLeft={0}
  stickyBottom={0}
  zIndex={60}
>
  Total
</Cell>
<Cell
  bold
  align="center"
  stickyLeft={hasOpenDayDetail ? DAY_COL : null}
  stickyBottom={0}
  zIndex={hasOpenDayDetail ? 55 : 30}
>
  {n0.format(periodTableTotals.fuelingEvents || 0)}
</Cell>
{!isUSA && <Cell bold stickyBottom={0} zIndex={30}>{n1.format(totalColFuelDisplay)}</Cell>}
{!isUSA && <Cell bold stickyBottom={0} zIndex={30}>{n1.format(totalMonterreyFuelDisplay)}</Cell>}
{!isUSA && <Cell bold stickyBottom={0} zIndex={30}>{n1.format(totalGasomaxFuelDisplay)}</Cell>}
{!isUSA && <Cell bold stickyBottom={0} zIndex={30}>{n1.format(totalOxxoFuelDisplay)}</Cell>}
{!isUSA && <Cell bold stickyBottom={0} zIndex={30}>{n1.format(totalLovesFuelDisplay)}</Cell>}
{!isUSA && <Cell bold stickyBottom={0} zIndex={30}>{n1.format(totalArguiFuelDisplay)}</Cell>}
<Cell bold bg="#c9e8c7" stickyBottom={0} zIndex={30}>{n1.format(totalTotalFuelDisplay)}</Cell>
<Cell bold bg="#9fc4e8" right={false} stickyBottom={0} zIndex={30}>{n1.format(totalDistanceDisplay)}</Cell>
<Cell
  bold
  align="center"
  right={false}
  bg={getFuelEffColor(periodTableTotals.fuelEff)}
  stickyRight={0}
  stickyBottom={0}
  zIndex={50}
>
  {n1.format(totalFuelEffDisplay)} {efficiencyLabel(unitSystem)}
</Cell>
                    <Cell bold align="right" right={false} bg="#00e600" stickyBottom={0} zIndex={30}>
                      {formatMoney(totalSavingsDisplay, unitSystem)}
                    </Cell>
                    {!isUSA && 
                    <Cell
  bold
  align="right"
  right={false}
  bg={getVarianceBg(periodTableTotals.variance)}
  stickyBottom={0} zIndex={30}
>
  {n1.format(totalVarianceDisplay)}
</Cell>
}
<Cell bold align="center" bg="#1cff1c" stickyBottom={0} zIndex={30}>
  {periodTableTotals.idlePercent}%
</Cell>

<Cell bold align="right" stickyBottom={0} zIndex={30}>
  {n1.format(periodTableTotals.idleHours)}
</Cell>
                </Box>
                </Box>
            </Paper>
            <Box
  onWheel={(e) => {
    if (e.deltaY !== 0) {
      e.currentTarget.scrollLeft += e.deltaY;
    }
  }}
  sx={{
  display: "flex",
  alignItems: "center",
  gap: 1.2,
  overflowX: "auto",
  overflowY: "hidden",
  whiteSpace: "nowrap",

  border: "none",
  bgcolor: "transparent",

  px: 1,
  py: 1,

  scrollbarWidth: "thin",
  scrollbarColor: "rgba(120,120,120,.45) transparent",

  "&::-webkit-scrollbar": {
    height: 8,
  },

  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },

  "&::-webkit-scrollbar-thumb": {
    background: "rgba(120,120,120,.45)",
    borderRadius: 20,
  },

  "&::-webkit-scrollbar-thumb:hover": {
    background: "rgba(90,90,90,.65)",
  },
}}
>
  <Button
    variant="outlined"
    onClick={() => handleRefresh(false)}
    disabled={loading || refreshing}
    startIcon={
      <SyncIcon
        sx={{
          animation: refreshing ? "spin 1s linear infinite" : "none",
        }}
      />
    }
    sx={{
      minWidth: 105,
      height: 36,
      textTransform: "none",
      fontWeight: 800,
    }}
  >
    Refresh
  </Button>
  <Button
    variant="outlined"
    onClick={handleOpenSource}
    endIcon={<KeyboardArrowDownIcon />}
    sx={{
      minWidth: 110,
      height: 36,
      borderRadius: 1.4,
      textTransform: "none",
      fontWeight: 800,
      fontSize: 13,
      px: 1.6,
    }}
  >
    {countryMode}
  </Button>

  <Menu
    anchorEl={anchorElSource}
    open={openSource}
    onClose={handleCloseSource}
    PaperProps={{
      sx: {
        mt: 0.5,
        minWidth: 140,
        borderRadius: 1.5,
        boxShadow: "0 8px 24px rgba(0,0,0,.18)",
      },
    }}
  >
    <MenuItem
      onClick={() => {
        setCountryMode("MEX");
        setActiveDriversMap({});
        setSelectedCompanies([]);
        clearCrossFilters();
        handleCloseSource();

        fetch(
          null,
          null,
          null,
          null,
          rankingAssets,
          rankingDrivers,
          "MEX"
        );
      }}
    >
      MEX
    </MenuItem>

    <MenuItem
      onClick={async () => {
        handleCloseSource();
        setSelectedCompanies([]);
        clearCrossFilters();
        setData([]);

        const drivers = await getDriversActivos();
        const tractors = await getUsaTractors();

        setCountryMode("USA");
        
        
        await fetch(
          null,
          null,
          null,
          null,
          rankingAssets,
          rankingDrivers,
          "USA",
          tractors
        );
      }}
    >
      USA
    </MenuItem>
  </Menu>

  <TextField
    select
    size="small"
    value={selectedYear}
    onChange={(e) => {
      setSelectedYear(Number(e.target.value));
      clearCrossFilters();
    }}
    SelectProps={{
      displayEmpty: true,
    }}
    sx={{
      width: 115,
      minWidth: 115,
      flexShrink: 0,
      bgcolor: "transparent",
      borderRadius: 1.4,

      "& .MuiOutlinedInput-root": {
        height: 36,
        borderRadius: 1.4,
        fontWeight: 800,
        fontSize: 13,
        color: "#0b69c7",
        bgcolor: "transparent",

        "& fieldset": {
          borderColor: "#6aa9ee",
        },

        "&:hover fieldset": {
          borderColor: "#1976d2",
        },

        "&.Mui-focused fieldset": {
          borderColor: "#1976d2",
          borderWidth: "1px",
        },
      },

      "& .MuiSelect-select": {
        py: 0,
        display: "flex",
        alignItems: "center",
        minWidth: 0,
      },

      "& .MuiSvgIcon-root": {
        color: "#0b69c7",
      },
    }}
  >
    {availableYears.map((year) => (
      <MenuItem key={year} value={year}>
        {year}
      </MenuItem>
    ))}
  </TextField>

  

  <Menu
    anchorEl={anchorElView}
    open={openView}
    onClose={handleCloseView}
    PaperProps={{
      sx: {
        mt: 0.5,
        minWidth: 180,
        borderRadius: 1.5,
        boxShadow: "0 8px 24px rgba(0,0,0,.18)",
      },
    }}
  >
    <MenuItem
      onClick={() => {
        navigate("/diesel/kpi/driver", {
          state: {
            data: filteredData,
            unitSystem,
            countryMode,
          },
        });

        handleCloseView();
      }}
    >
      DRIVER
    </MenuItem>

    <MenuItem
      onClick={() => {
        navigate("/diesel/kpi/asset", {
          state: {
            data: filteredData,
            unitSystem,
            countryMode,
          },
        });

        handleCloseView();
      }}
    >
      ASSET
    </MenuItem>

    <MenuItem
      onClick={() => {
        navigate("/diesel/kpi/idle");
        handleCloseView();
      }}
    >
      IDLE
    </MenuItem>
  </Menu>

  <Button
    variant="outlined"
    onClick={handleOpenUnits}
    endIcon={<KeyboardArrowDownIcon />}
    sx={{
      minWidth: 130,
      height: 36,
      borderRadius: 1.4,
      textTransform: "none",
      fontWeight: 800,
      fontSize: 13,
      px: 1.6,
    }}
  >
    {unitSystem === "imperial"
      ? "Imperial"
      : "Métrico"}
  </Button>

  <Menu
    anchorEl={anchorElUnits}
    open={openUnits}
    onClose={handleCloseUnits}
    PaperProps={{
      sx: {
        mt: 0.5,
        minWidth: 170,
        borderRadius: 1.5,
        boxShadow: "0 8px 24px rgba(0,0,0,.18)",
      },
    }}
  >
    <MenuItem
      onClick={() => {
        setUnitSystem("imperial");
        setTargetMpg(7);
        handleCloseUnits();
      }}
    >
      Imperial
    </MenuItem>

    <MenuItem
      onClick={() => {
        setUnitSystem("metric");
        setTargetMpg(7);
        handleCloseUnits();
      }}
    >
      Métrico
    </MenuItem>
  </Menu>

  <Button
    variant="outlined"
    onClick={handleOpenView}
    endIcon={<KeyboardArrowDownIcon />}
    sx={{
      minWidth: 100,
      height: 36,
      borderRadius: 1.4,
      textTransform: "none",
      fontWeight: 800,
      fontSize: 13,
      px: 1.6,
    }}
  >
    OTHERS
  </Button>
</Box>
          </Box>

          

          <Paper
  sx={{
    borderRadius: 0,
    border: "1px solid #d5d5d5",
    bgcolor: "#f4f4f4",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  }}
>
  <Box
    sx={{
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      overflowX: "auto",
      "&::-webkit-scrollbar": {
        height: 8,
        width: 8,
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#bdbdbd",
        borderRadius: 8,
      },
      "&::-webkit-scrollbar-track": {
        background: "#efefef",
      },
    }}
  >
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: truckGridColumns,
        minWidth: `${truckGridMinWidth}px`,
        position: "sticky",
        top: 0,
        zIndex: 2,
      }}
    >
      <SortHeader
        field="truck"
        activeField={truckSortField}
        direction={truckSortDirection}
        onClick={handleTruckSort}
        variant="dark"
      >
        Truck #
      </SortHeader>

      <SortHeader
        field="fuelEff"
        activeField={truckSortField}
        direction={truckSortDirection}
        onClick={handleTruckSort}
        align="center"
        variant="dark"
      >
        Fuel Eff.
      </SortHeader>

      {!isUSA && (
      <SortHeader
        field="make"
        activeField={truckSortField}
        direction={truckSortDirection}
        onClick={handleTruckSort}
        right={false}
        variant="dark"
      >
        Make
      </SortHeader>
      )}
    </Box>

    {sortedTruckRanking.map((r) => {
  const isSelected = selectedTrucks.includes(String(r.truck));
  const fuelEffDisplay = convertEfficiencyFromMpg(r.fuelEff, unitSystem);

  return (
    <Box
      key={r.truck}
      onClick={() => toggleSelection(String(r.truck), setSelectedTrucks)}
      sx={{
        display: "grid",
        gridTemplateColumns: truckGridColumns,
        minWidth: `${truckGridMinWidth}px`,
        cursor: "pointer",
      }}
    >
      <Cell align="right" bg={isSelected ? "#dbeafe" : "#f3f3f3"}>
        {r.truck}
      </Cell>

      <Cell
        bg={isSelected ? "#c7defc" : getFuelColor(r.fuelEff)}
        align="center"
      >
        {n1.format(fuelEffDisplay)} {efficiencyLabel(unitSystem)}
      </Cell>

      {!isUSA &&
      <Cell align="left" right={false} bg={isSelected ? "#dbeafe" : "#f3f3f3"}>
        {r.make}
      </Cell>
      }
    </Box>
  );
})}
  </Box>
</Paper>

          <Box
  sx={{
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 220px",
    gap: 1,
    overflow: "hidden",
  }}
>
            <Box
  sx={{
    minWidth: 0,
    width: "100%",
    display: "grid",
    gridTemplateRows: "fit-content(100px) minmax(0, 1fr)",
    gap: 1,
    overflow: "hidden",
    //alignItems: "start",
  }}
>
              <Box sx={{ minWidth: 0 }}>
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
    }}
  >
    <SortHeader
      field="company"
      activeField={companySortField}
      direction={companySortDirection}
      onClick={handleCompanySort}
      variant="dark"
    >
      {isUSA ? "Group" : "Company"}
    </SortHeader>

    <SortHeader
      field="fuelEff"
      activeField={companySortField}
      direction={companySortDirection}
      onClick={handleCompanySort}
      variant="dark"
      right={false}
    >
      Fuel Eff.
    </SortHeader>
  </Box>

  {sortedCompanyRanking.map((row) => {
  const isSelected = selectedCompanies.includes(row.company);
  const fuelEffDisplay = convertEfficiencyFromMpg(row.fuelEff, unitSystem);

  return (
    <Box
      key={row.company}
      onClick={() => toggleSelection(row.company, setSelectedCompanies)}
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        cursor: "pointer",
      }}
    >
      <Cell bg={isSelected ? "#dbeafe" : "#f3f3f3"}>
        {row.company}
      </Cell>

      <Cell
        bg={isSelected ? "#c7defc" : getFuelEffColor(row.fuelEff)}
        right={false}
      >
        {n1.format(fuelEffDisplay)} {efficiencyLabel(unitSystem)}
      </Cell>
    </Box>
  );
})}
</Box>



             <Paper
  sx={{
    borderRadius: 0,
    border: "1px solid #d5d5d5",
    bgcolor: "#f3f3f3",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    minWidth: 0,
    overflow: "hidden",
    height: "100%",
  }}
>
  <Box
  sx={{
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    width: "100%",
    height: 0,
    overflowX: "auto",
    overflowY: "auto",
    position: "relative",
    "&::-webkit-scrollbar": {
      height: 8,
      width: 8,
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#bdbdbd",
      borderRadius: 8,
    },
    "&::-webkit-scrollbar-track": {
      background: "#efefef",
    },
  }}
>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: driverGridColumns,
        width: "max-content",
        minWidth: `${driverGridMinWidth}px`,
      }}
    >
      <SortHeader
        field="rank"
        activeField={driverSort.field}
        direction={driverSort.direction}
        onClick={handleDriverSort}
        align="center"
        variant="dark"
      >
        Rank
      </SortHeader>

      <SortHeader
        field="driver"
        activeField={driverSort.field}
        direction={driverSort.direction}
        onClick={handleDriverSort}
        align="left"
        variant="dark"
      >
        Driver
      </SortHeader>

      <SortHeader
        field="fuelEff"
        activeField={driverSort.field}
        direction={driverSort.direction}
        onClick={handleDriverSort}
        align="center"
        variant="dark"
      >
        Fuel Eff.
      </SortHeader>

      {!isUSA && (
      <SortHeader
        field="statusLabel"
        activeField={driverSort.field}
        direction={driverSort.direction}
        onClick={handleDriverSort}
        align="left"
        right={false}
        variant="dark"
      >
        MPG Review Status
      </SortHeader>
      )}

{sortedDriverRanking.map((r) => {
  const driverKey = r.driverKey;
  const driverName = r.driver;

  const isSelected =
    selectedDrivers.includes(driverKey);
  const fuelEffDisplay = convertEfficiencyFromMpg(r.fuelEff, unitSystem);

  return (
    <React.Fragment key={`${r.rank}-${r.driver}`}>
      <Cell
        align="center"
        bg={isSelected ? "#dbeafe" : "#f3f3f3"}
      >
        <Box
          onClick={() =>
            toggleSelection(driverKey, setSelectedDrivers)
          }
          sx={{ cursor: "pointer", width: "100%" }}
        >
          {r.rank}
        </Box>
      </Cell>

      <Cell align="left" bg={isSelected ? "#dbeafe" : "#f3f3f3"}>
        <Box
          onClick={() =>
            toggleSelection(driverKey, setSelectedDrivers)
          }
          title={driverName}
          sx={{
            cursor: "pointer",
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {driverName}
        </Box>
      </Cell>

      <Cell
        bg={isSelected ? "#c7defc" : getFuelEffColor(r.fuelEff)}
        align="center"
        bold
      >
        <Box
          onClick={() =>
            toggleSelection(driverKey, setSelectedDrivers)
          }
          sx={{ cursor: "pointer", width: "100%" }}
        >
          {n1.format(fuelEffDisplay)} {efficiencyLabel(unitSystem)}
        </Box>
      </Cell>

      {!isUSA && (
      <Cell
        align="left"
        bg={isSelected ? "#dbeafe" : statusBg(r.statusCode)}
        color={statusColor(r.statusCode)}
        right={false}
      >
        <Box
          onClick={() =>
            toggleSelection(driverKey, setSelectedDrivers)
          }
          title={r.statusLabel}
          sx={{
            cursor: "pointer",
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {r.statusLabel}
        </Box>
      </Cell>
      )}
    </React.Fragment>
  );
})}
    </Box>
  </Box>
</Paper>
            </Box>

           <Box
  sx={{
    minWidth: 0,
    display: "grid",
    gridTemplateRows: "150px auto",
    gap: 1,
    overflow: "hidden",
    alignContent: "start",
  }}
>
  <Paper
  elevation={0}
  sx={{
    borderRadius: "18px",
    border: "1px solid rgba(180,150,40,.35)",

    bgcolor: getFuelEffColor(overallFuelEff),

    background: `linear-gradient(
      135deg,
      ${getFuelEffColor(overallFuelEff)} 0%,
      rgba(255,255,255,.35) 100%
    )`,

    boxShadow: "0 8px 20px rgba(0,0,0,.12)",
    minHeight: 150,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    overflow: "hidden",
  }}
>
  <Typography
    sx={{
      fontSize: 58,
      lineHeight: 0.95,
      fontWeight: 950,
      color: "#222",
    }}
  >
    {n1.format(convertEfficiencyFromMpg(overallFuelEff, unitSystem))}
  </Typography>

  <Typography
    sx={{
      fontSize: 34,
      lineHeight: 1,
      fontWeight: 900,
      color: "#222",
    }}
  >
    {efficiencyLabel(unitSystem)}
  </Typography>
</Paper>

  <Paper
    sx={{
      borderRadius: 0,
      border: "1px solid #d5d5d5",
      bgcolor: "#f8f8f8",
      p: 0.7,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      minHeight: 0,
    }}
  >
    <TextField
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      size="small"
      placeholder="Search"
      InputProps={{ startAdornment: <SearchIcon sx={{ color: "#777", mr: 1 }} /> }}
      sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: 1, height: 40 } }}
    />
    <List dense sx={{ flex: 1, minHeight: 0, overflowY: "auto", mt: 0.8, pt: 0 }}>
      {filteredSearch.map((item) => {
        const isSelected = selectedDrivers.includes(item.key);

        return (
          <ListItemButton
            key={item.key}
            onClick={() =>
              toggleSelection(item.key, setSelectedDrivers)
            }
            sx={{
              mb: 0.65,
              border: "1px solid #bdbdbd",
              borderRadius: 1,
              py: 0.55,
              bgcolor: isSelected ? "#dbeafe" : "#f6f6f6",
              "&:hover": {
                bgcolor: isSelected ? "#c7defc" : "#eeeeee",
              },
            }}
          >
            <ListItemText
              primary={item.name}
              primaryTypographyProps={{ fontSize: 13 }}
            />
          </ListItemButton>
        );
      })}
    </List>
  </Paper>
</Box>
          </Box>
        </Box>
        )}
      </Paper>
    </Box>
    </>
  );
}
